import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Copy, MagnifyingGlass, FloppyDisk, FileXls, CheckCircle, Lock, Plus, DownloadSimple, Rocket, ArrowRight, PencilSimple, Info, Eye, EyeSlash } from '@phosphor-icons/react';
import api, { getBookingHistoryRow, getAmazonAutoLockSetting } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import TOTPWidget from '../components/TOTPWidget';
import AutoDistributeCardsDialog from '../components/booking/AutoDistributeCardsDialog';
import SubmitReportDialog from '../components/booking/SubmitReportDialog';
import LoginPasswordWidget from '../components/LoginPasswordWidget';
import KeyboardShortcutsOverlay from '../components/KeyboardShortcutsOverlay';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';

const SHEET_SHORTCUTS = [
  { keys: ['Enter'], desc: 'Move to next row (same column)' },
  { keys: ['Shift', 'Enter'], desc: 'Move to previous row (same column)' },
  { keys: ['Tab'], desc: 'Move to next column' },
  { keys: ['Shift', 'Tab'], desc: 'Move to previous column' },
  { keys: ['Ctrl', 'D'], desc: 'Copy value from row above (fill down)' },
  { keys: ['Alt', '↓'], desc: 'Move down (works on dropdowns too)' },
  { keys: ['Alt', '↑'], desc: 'Move up (works on dropdowns too)' },
  { keys: ['Ctrl', 'S'], desc: 'Submit Final (with confirmation)' },
  { keys: ['Esc'], desc: 'Blur current cell' },
  { keys: ['?'], desc: 'Toggle this shortcuts help' },
];

// User preference: no thousands separator in amounts (per workspace feedback).
// Display "₹1842" instead of "₹1,842" for cleaner copy-paste / mental math.
// Rounding policy: HALF_UP for booking payable amounts so 4552.5 → 4553 (not 4552).
const roundUp = (n) => Math.floor(Number(n || 0) + 0.5);
const fmt = (n) => '₹' + roundUp(n).toString();
const STATUS_COLOR = { success: 'bg-green-100 text-green-800 border-green-300', pending: 'bg-amber-100 text-amber-800 border-amber-300', failed: 'bg-red-100 text-red-700 border-red-300' };
const ROW_FILL = { success: 'bg-green-50/50', pending: 'bg-amber-50/50', failed: 'bg-red-50/50' };

// Pre-canned booking-site URLs per OMC. Used by 1-click Book.
const OMC_BOOKING_URLS = {
  HPCL: 'https://myhpgas.in/myHPGas/HPGas/customerLogin.do',
  'BHARAT GAS': 'https://my.ebharatgas.com/bharatgas/Customer/CustomerHome.aspx',
  BHARAT: 'https://my.ebharatgas.com/bharatgas/Customer/CustomerHome.aspx',
  INDANE: 'https://cx.indianoil.in/webcenter/portal/Customer',
};
const omcBookingUrl = (oc) => {
  const k = (oc || '').toUpperCase().trim();
  return OMC_BOOKING_URLS[k] || OMC_BOOKING_URLS[k.replace(/\s+/g, ' ')] || null;
};

function copy(text, label) {
  if (!text) return;
  const str = String(text);
  // Modern clipboard API with secure-context fallback to legacy execCommand,
  // so the copy works even inside embedded iframes / older browsers.
  const showToast = () => toast.success(`${label || 'Copied'}: ${str}`);
  try {
    if (navigator?.clipboard?.writeText && window.isSecureContext) {
      navigator.clipboard.writeText(str).then(showToast).catch(() => {
        const ta = document.createElement('textarea');
        ta.value = str; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); showToast(); } catch {}
        document.body.removeChild(ta);
      });
      return;
    }
    const ta = document.createElement('textarea');
    ta.value = str; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    showToast();
  } catch {
    showToast(); // at least show value in toast so user can copy manually
  }
}

/**
 * ProtectedCell — read-only-with-copy by default. Click pencil to unlock for editing.
 * Prevents accidental edits / deletions of critical fields like LPG ID.
 * Click on the value also copies it to clipboard.
 */
function ProtectedCell({
  rowId, field, value, isEditing, onStartEdit, onStopEdit, onChange,
  onCellKeyDown, locked, copy: copyFn, copyLabel, mono, width, placeholder, testIdPrefix = 'cell',
}) {
  if (isEditing && !locked) {
    return (
      <Input
        data-nav-id={`nav-${rowId}-${field}`}
        autoFocus
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onStopEdit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Escape') {
            e.preventDefault();
            onStopEdit();
          } else {
            onCellKeyDown(e, rowId, field);
          }
        }}
        placeholder={placeholder}
        className={`h-7 text-xs ${mono ? 'font-mono' : ''} ${width || ''}`}
        data-testid={`${testIdPrefix}-edit-${rowId}-${field}`}
      />
    );
  }
  const displayVal = value || (placeholder ? '' : '—');
  return (
    <div
      className={`group flex items-center gap-1 h-7 px-2 rounded border bg-white/60 hover:bg-white hover:border-violet-300 transition-colors ${width || ''} ${locked ? 'opacity-50' : ''}`}
      data-testid={`${testIdPrefix}-readonly-${rowId}-${field}`}
    >
      <span
        onClick={() => copyFn(value, copyLabel)}
        className={`flex-1 truncate cursor-copy text-xs ${mono ? 'font-mono' : ''} ${value ? 'text-gray-800' : 'text-gray-400'}`}
        title={value ? `Click to copy: ${value}` : placeholder}
      >
        {displayVal || placeholder || '—'}
      </span>
      {value && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); copyFn(value, copyLabel); }}
          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-violet-600 transition-opacity"
          title="Copy"
          data-testid={`${testIdPrefix}-copy-${rowId}-${field}`}
        >
          <Copy size={12} />
        </button>
      )}
      {!locked && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onStartEdit(); }}
          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-amber-600 transition-opacity"
          title="Edit (locked by default to prevent accidental cuts)"
          data-testid={`${testIdPrefix}-pencil-${rowId}-${field}`}
        >
          <PencilSimple size={12} />
        </button>
      )}
    </div>
  );
}

/**
 * RowAuditPopover — 1-click audit ke liye small ⓘ icon. Click karte hi
 * fetch karta hai poori audit info: account email, card last4, sub-staff,
 * file, time. Submitted rows ke liye most useful (booking already done).
 */
function RowAuditPopover({ rowId, hasData }) {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const handleOpen = async (isOpen) => {
    setOpen(isOpen);
    if (isOpen && !audit && rowId) {
      setLoading(true);
      try {
        const { data } = await getBookingHistoryRow(rowId);
        setAudit(data);
      } catch (err) {
        toast.error('Audit info load nahi hua');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`text-[11px] ${hasData ? 'text-violet-600 hover:text-violet-800' : 'text-gray-300'} transition-colors`}
          title="1-click audit: account, card, time"
          data-testid={`row-audit-btn-${rowId}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Info size={14} weight="fill" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-3 text-xs" data-testid={`row-audit-pop-${rowId}`}>
        {loading ? (
          <div className="text-gray-500">Loading audit…</div>
        ) : audit ? (
          <div className="space-y-2">
            <div className="font-bold text-violet-700 flex items-center gap-1">
              <Info size={12} weight="fill" /> Booking Audit
            </div>
            {/* Platform-specific account block */}
            {audit.account_email || audit.account_password ? (() => {
              const isAmz = (audit.account_platform || '').toLowerCase().includes('amazon');
              const palette = isAmz
                ? { bg: 'from-orange-50 to-amber-50', border: 'border-orange-300', tag: 'bg-orange-600', label: '🟠 AMAZON' }
                : { bg: 'from-pink-50 to-rose-50', border: 'border-pink-300', tag: 'bg-pink-600', label: '🔴 TATANEU' };
              const cp = (val, lbl) => {
                if (!val) return;
                navigator.clipboard.writeText(val);
                toast.success(`${lbl} copied`);
              };
              return (
                <div className={`rounded-md bg-gradient-to-r ${palette.bg} border ${palette.border} p-2 space-y-1`}>
                  <div className="flex items-center gap-1">
                    <span className={`text-[9px] font-bold text-white px-1.5 py-0.5 rounded ${palette.tag}`}>{palette.label}</span>
                    <span className="text-[9px] text-gray-500">click to copy</span>
                  </div>
                  <div onClick={() => cp(audit.account_email, isAmz ? 'Email' : 'Account')} className="cursor-copy hover:bg-white/60 rounded px-1 py-0.5">
                    <div className="text-[9px] text-gray-500 uppercase">{isAmz ? 'Email' : 'Account'}</div>
                    <div className="font-mono text-blue-700 font-semibold break-all text-[11px]">{audit.account_email || '-'}</div>
                  </div>
                  <div className="px-1 py-0.5">
                    <div className="text-[9px] text-gray-500 uppercase flex items-center gap-1">
                      Login Password
                      {audit.account_totp_secret && <span className="text-[8px] bg-violet-100 text-violet-700 px-1 rounded">PWD + OTP</span>}
                    </div>
                    {audit.account_totp_secret ? (
                      <div className="mt-1 flex flex-col gap-1">
                        <LoginPasswordWidget
                          password={audit.account_password}
                          secret={audit.account_totp_secret}
                          label={isAmz ? 'Amazon Login (PWD 1)' : 'TataNeu Login'}
                          compact
                          testId={`audit-login-pwd-${rowId}`}
                        />
                        {audit.account_password2 && (
                          <LoginPasswordWidget
                            password={audit.account_password2}
                            secret={audit.account_totp_secret}
                            label="Amazon Login (PWD 2)"
                            compact
                            testId={`audit-login-pwd2-${rowId}`}
                          />
                        )}
                      </div>
                    ) : (
                      <div onClick={() => cp(audit.account_password, 'Password')} className="cursor-copy font-mono text-amber-700 font-bold break-all text-[11px]">
                        {audit.account_password || '-'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })() : (
              <div className="text-[10px] text-gray-400 italic border border-dashed border-gray-300 rounded p-1.5 bg-gray-50">
                Account info nahi mila (purani booking — naye bookings me dikhe ga)
              </div>
            )}
            <div className="grid grid-cols-2 gap-1.5">
              <div className="text-gray-500">LPG ID</div>
              <div className="font-mono font-bold">{audit.consumer_id || '-'}</div>
              <div className="text-gray-500">Status</div>
              <div className="font-bold uppercase">{audit.status || 'draft'}</div>
              <div className="text-gray-500">Sub-staff</div>
              <div>{audit.sub_staff_name || '-'}</div>
              <div className="text-gray-500">Card</div>
              <div className="font-mono">{audit.card_last4 ? `****${audit.card_last4}` : '-'}</div>
              <div className="text-gray-500">Card Vendor</div>
              <div>{audit.card_vendor_name || '-'}</div>
              <div className="text-gray-500">Submitted At</div>
              <div>{audit.submitted_at ? new Date(audit.submitted_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' }) : '-'}</div>
            </div>
            {audit.notes && (
              <div className="pt-1 border-t text-gray-600">
                <b>Notes:</b> {audit.notes}
              </div>
            )}
          </div>
        ) : (
          <div className="text-gray-400">No data</div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function BookingWorkspacePage() {
  const { fileId } = useParams();
  const { user } = useAuth();
  const isStaffOrSub = user?.role === 'staff' || user?.role === 'sub_staff';
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [account, setAccount] = useState(null);
  const [card, setCard] = useState(null);
  const [accSearch, setAccSearch] = useState('');
  const [cardSearch, setCardSearch] = useState('');
  const [showAccPicker, setShowAccPicker] = useState(false);
  const [showCardPicker, setShowCardPicker] = useState(false);
  const [accList, setAccList] = useState([]);
  const [cardList, setCardList] = useState([]);

  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('sheet');  // 'list' | 'sheet'
  const [hideSubmittedGroups, setHideSubmittedGroups] = useState(true);  // Item 4 — auto-hide submitted groups
  const [groupSize, setGroupSize] = useState(5);  // bookings per card group
  const [groupCards, setGroupCards] = useState({});  // groupIdx → card object
  const [groupAccounts, setGroupAccounts] = useState({});  // groupIdx → account object
  const [activeGroupIdx, setActiveGroupIdx] = useState(0);  // which group's picker is open
  // Item #3 (Feb 2026 user spec): Per-group credentials visibility toggle.
  // Default = collapsed (only Amazon email shown, password/TOTP/card hidden).
  // Once user clicks "Show", a group expands and stays so for the session.
  const [expandedCreds, setExpandedCreds] = useState({});  // groupIdx → bool

  // Master toggle (admin-controlled) — when OFF, the entire Amazon account /
  // Login / Card / Exp / CVV / Card Pwd strip is hidden across all groups so
  // staff aren't distracted by stale empty fields while Amazon work is paused.
  const [amazonLockEnabled, setAmazonLockEnabled] = useState(true);
  useEffect(() => {
    getAmazonAutoLockSetting().then(({ data }) => setAmazonLockEnabled(!!data.enabled)).catch(() => {});
  }, []);

  // Workspace zoom (Google Sheets style — persisted per browser)
  const [zoom, setZoom] = useState(() => {
    try { const v = Number(localStorage.getItem('bw_zoom') || '100'); return v >= 50 && v <= 200 ? v : 100; }
    catch { return 100; }
  });
  useEffect(() => { try { localStorage.setItem('bw_zoom', String(zoom)); } catch {} }, [zoom]);

  // Default booking platform (TataNeu / JioFinance / Normal) — applied to every
  // draft row on load. Per-row override lives inside each row (orange border).
  const PLATFORM_OPTIONS = ['TataNeu', 'JioFinance', 'Normal'];
  const [defaultPlatform, setDefaultPlatform] = useState(() => {
    try { return localStorage.getItem('bw_default_platform') || 'TataNeu'; }
    catch { return 'TataNeu'; }
  });
  useEffect(() => { try { localStorage.setItem('bw_default_platform', defaultPlatform); } catch {} }, [defaultPlatform]);
  const [applyingPlatform, setApplyingPlatform] = useState(false);

  // Auto-switch view based on Amazon mode:
  //   ON  → 5/5 group "Sheet View" (existing flow, all credentials visible per group)
  //   OFF → flat Google-sheet style "List View" (no grouping; cards still shown per row)
  // User can manually flip view via the toggle if they want.
  useEffect(() => {
    setViewMode(amazonLockEnabled ? 'sheet' : 'list');
  }, [amazonLockEnabled]);

  // ─────────── Add-Rows / Import-from-checked dialogs ───────────
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [pasteRows, setPasteRows] = useState('');
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [checkedSources, setCheckedSources] = useState([]);
  const [importing, setImporting] = useState(false);

  // ─────────── Account swap (1-by-1 replace, mandatory reason) ───────────
  // Feb 2026: bulk picker hidden for staff/sub_staff. They click "Replace"
  // → confirm dialog with reason dropdown → server returns ONE next account.
  const [swapDialog, setSwapDialog] = useState(null);  // { groupIdx, current }
  const [swapReason, setSwapReason] = useState('');
  const [swapExtra, setSwapExtra] = useState('');
  const [swapping, setSwapping] = useState(false);
  const [quickSwapping, setQuickSwapping] = useState(null);  // groupIdx during ⚡ quick swap
  const SWAP_REASONS = [
    'Account already used',
    'Mobile number not available',
    'Invalid account details',
    'Password incorrect',
    'OTP not received',
    'Other',
  ];

  // ─────────── Bulk selection ───────────
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // ─────────── Cell edit-protection ───────────
  // Each cell is read-only-with-copy by default. Click the pencil to unlock.
  // Format: "${rowId}-${field}" or null
  const [editingCell, setEditingCell] = useState(null);
  const isEditing = (rowId, field) => editingCell === `${rowId}-${field}`;
  const startEdit = (rowId, field) => setEditingCell(`${rowId}-${field}`);
  const stopEdit = () => setEditingCell(null);

  // ─────────── Resume banner — find first un-submitted row ───────────
  const resumeInfo = useMemo(() => {
    if (!rows || rows.length === 0) return null;
    const submittedCount = rows.filter(r => r.submitted_at).length;
    if (submittedCount === 0) return null;
    const firstPending = rows.find(r => !r.submitted_at);
    if (!firstPending) return null; // all done
    const idx = rows.findIndex(r => r.id === firstPending.id);
    return {
      rowId: firstPending.id,
      consumer: firstPending.consumer_id,
      customerName: firstPending.customer_name,
      submitted: submittedCount,
      total: rows.length,
      humanIndex: idx + 1,
    };
  }, [rows]);

  const scrollToResume = () => {
    if (!resumeInfo) return;
    const el = document.querySelector(`[data-testid="exec-row-${resumeInfo.rowId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-4', 'ring-amber-300');
      setTimeout(() => el.classList.remove('ring-4', 'ring-amber-300'), 1800);
      const cell = el.querySelector('input, [data-testid^="cell-pencil-"]');
      if (cell) setTimeout(() => cell.focus?.(), 600);
    }
  };

  useEffect(() => {
    if (resumeInfo && rows.length > 0 && !loading) {
      const t = setTimeout(scrollToResume, 600);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const reload = useCallback(async () => {
    if (!fileId) return;
    setLoading(true);
    try {
      // Sequential: workspace endpoint may trigger heal-on-read which auto-locks
      // accounts. Wait for it BEFORE fetching active-account so the locked
      // account is reflected in the response.
      const w = await api.get(`/booking-execution/${fileId}`);
      setFile(w.data.file);
      // Per-user-request (Feb 2026): if row.amount is 0 and file has a
      // booking_rate set by admin/distributor, auto-fill it client-side so
      // every row shows the agency price without manual entry. We don't
      // persist this on the server — server still has the real value and
      // any explicit edit (Input onChange) saves the user's number.
      const rate = Number(w.data.file?.booking_rate) || 0;
      const rawRows = w.data.rows || [];
      const hydratedRows = rate > 0
        ? rawRows.map(r => ({ ...r, amount: Number(r.amount) > 0 ? r.amount : rate }))
        : rawRows;
      setRows(hydratedRows);
      const [a, c] = await Promise.all([
        api.get('/booking-pool/active-account?platform=any'),
        api.get(`/booking-pool/cards?file_id=${encodeURIComponent(fileId)}`),
      ]);
      setAccount(a.data || null);
      const firstCard = (c.data || []).find(x => !x.in_cooldown && x.available > 0);
      setCard(firstCard || (c.data || [])[0] || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, [fileId]);
  useEffect(() => { reload().then(() => { fetchAccList(); fetchCardList(); }); }, [reload]);

  const fetchAccList = async (q = '') => {
    try {
      const params = new URLSearchParams({ platform: 'any' });
      if (q) params.set('q', q);
      const { data } = await api.get(`/booking-pool/accounts?${params.toString()}`);
      setAccList(data || []);
    } catch (e) {}
  };
  const fetchCardList = async (q = '') => {
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (fileId) params.set('file_id', fileId);
      const { data } = await api.get(`/booking-pool/cards?${params.toString()}`);
      setCardList(data || []);
    } catch (e) {}
  };

  const [cardChunks, setCardChunks] = useState([]);   // NEW: card_rows for this file
  const [chunkSummary, setChunkSummary] = useState(null);

  const fetchCardChunks = useCallback(async () => {
    if (!fileId) return;
    try {
      const { data } = await api.get(`/card-rows?file_id=${encodeURIComponent(fileId)}`);
      setCardChunks(data.chunks || []);
      setChunkSummary(data.summary || null);
    } catch (e) {
      // Silently fail — chunk mode is optional. Endpoint may 404 / return empty.
      setCardChunks([]);
      setChunkSummary(null);
    }
  }, [fileId]);

  useEffect(() => { fetchCardChunks(); }, [fetchCardChunks]);

  const switchAccount = async (id) => {
    try {
      const { data } = await api.post(`/booking-pool/account/${id}/use`);
      setAccount(data);
      setShowAccPicker(false);
      toast.success('Account switched');
    } catch (err) { toast.error(err.response?.data?.detail || 'Switch failed'); }
  };

  // Apply the default booking platform to every draft row that doesn't already
  // have an explicit per-row override. Used both on first load and from the
  // top-bar "Apply to all rows" button.
  const applyDefaultPlatformToAll = useCallback(async (platform, opts = {}) => {
    const force = !!opts.force;
    const draftRows = rows.filter(r => (r.phase_status || 'draft') === 'draft');
    const targets = force
      ? draftRows
      : draftRows.filter(r => !(r.extras && r.extras.platform));
    if (targets.length === 0) return { applied: 0 };
    setApplyingPlatform(true);
    setRows(prev => prev.map(r => {
      if (!targets.find(t => t.id === r.id)) return r;
      return { ...r, extras: { ...(r.extras || {}), platform } };
    }));
    // Background PATCH for each target; ignore individual errors (best-effort).
    await Promise.all(targets.map(t =>
      api.patch(`/booking-execution/row/${t.id}`, { booking_platform: platform }).catch(() => null)
    ));
    setApplyingPlatform(false);
    return { applied: targets.length };
  }, [rows]);

  // Auto-apply default platform on initial load (only fills empty rows; never
  // overwrites a row that has its own override).
  const platformAutoAppliedRef = useRef(false);
  useEffect(() => {
    if (platformAutoAppliedRef.current) return;
    if (!rows.length || !defaultPlatform) return;
    platformAutoAppliedRef.current = true;
    applyDefaultPlatformToAll(defaultPlatform, { force: false }).catch(() => {});
  }, [rows.length, defaultPlatform, applyDefaultPlatformToAll]);

  const updateRow = async (rowId, patch) => {
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, ...patch } : r));
    try {
      const body = { ...patch };
      if (account && (patch.status === 'success' || patch.status === 'pending')) {
        body.account_id = account.id;
        if (card) body.card_id = card.id;
      }
      const { data } = await api.patch(`/booking-execution/row/${rowId}`, body);
      setRows(prev => prev.map(r => r.id === rowId ? data : r));
      // If status flipped to success, increment account/card usage
      if (patch.status === 'success' && account) {
        try {
          const { data: a2 } = await api.post(`/booking-pool/account/${account.id}/increment`);
          if (a2.in_cooldown || a2.available <= 0) {
            const { data: nxt } = await api.get('/booking-pool/active-account?platform=any');
            setAccount(nxt || null);
            toast.info('Account auto-switched (limit reached)');
          } else { setAccount(a2); }
          if (card) {
            const { data: c2 } = await api.post(`/booking-pool/card/${card.id}/increment`);
            setCard(c2);
          }
        } catch (e) {}
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); reload(); }
  };

  const [showSubmitReport, setShowSubmitReport] = useState(false);
  const [showAutoDistribute, setShowAutoDistribute] = useState(false);
  // Per-group account pre-balance (e.g., Amazon gift balance entered manually).
  // Keyed by group index. Persists in browser session only.
  const [groupPrebalance, setGroupPrebalance] = useState({});

  const submit = async () => {
    if (!window.confirm('Saari rows lock ho jayengi. Confirm?')) return;
    setSubmitting(true);
    try {
      // Feb 2026 fix: top-level submit now iterates groups so each group's
      // currently-assigned account+card gets stamped on ITS rows (not a single
      // file-wide account that blanks out per-group assignments).
      // Eligible = !submitted_at AND has status AND phase_status='draft' (skip
      // rows already handed-off for review).
      const eligibleRows = rows.filter(r => {
        if (r.submitted_at) return false;
        if (!r.status) return false;
        const phase = r.phase_status || 'draft';
        return phase === 'draft';
      });
      if (eligibleRows.length === 0) {
        const inReview = rows.filter(r => r.phase_status === 'pending_review').length;
        if (inReview > 0) {
          toast.error(`📨 ${inReview} rows pehle se review ke liye bheje hue hain — main staff approval ka wait karo`);
        } else {
          toast.error('Koi eligible row nahi hai');
        }
        return;
      }
      // Re-build groups exactly as the UI renders them (same ordering + size).
      const gSize = Math.max(1, groupSize || 5);
      const groupsArr = [];
      for (let i = 0; i < eligibleRows.length; i += gSize) {
        groupsArr.push(eligibleRows.slice(i, i + gSize));
      }
      let total = 0;
      let lastResp = null;
      for (let i = 0; i < groupsArr.length; i++) {
        const gRows = groupsArr[i];
        const groupAcc = groupAccounts[i] || account;
        const groupCrd = groupCards[i] || card;
        const payload = {
          row_ids: gRows.map(r => r.id),
          account_id: groupAcc?.id || null,
          card_id: groupCrd?.id || null,
          prebalance_used: Number(groupPrebalance[i] || 0),
        };
        const { data } = await api.post(`/booking-execution/${fileId}/submit`, payload);
        total += data.submitted || 0;
        lastResp = data;
      }
      if (lastResp?.is_handoff) {
        toast.success(
          `📨 ${total} rows main staff ko bhej diye for review.\n` +
          `Approval ke baad Booking History me dikh jayenge.`,
          { duration: 5000 }
        );
      } else {
        toast.success(`✅ ${total} rows final submitted${lastResp?.all_done ? ' · file Completed' : ''}`);
      }
      await reload();
      await fetchAccList();
      await fetchCardList();
      setShowSubmitReport(true);
      if (lastResp?.all_done) {
        setTimeout(() => { window.location.href = '/assigned-files'; }, 5000);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Submit failed'); }
    finally { setSubmitting(false); }
  };

  const submitGroup = async (groupRows, gIdx, opts = {}) => {
    // Eligible = row has a status (success/pending/failed) AND is still in
    // 'draft' phase (not yet handed-off for review or finally submitted).
    // For sub_staff specifically: rows already in pending_review are HANDED-OFF
    // and cannot be re-submitted until main staff rejects them back to draft.
    const eligibleIds = groupRows.filter(r => {
      if (r.submitted_at) return false;
      if (!r.status) return false;
      const phase = r.phase_status || 'draft';
      return phase === 'draft';
    }).map(r => r.id);
    if (eligibleIds.length === 0) {
      const inReview = groupRows.filter(r => r.phase_status === 'pending_review').length;
      if (inReview > 0) {
        toast.error(`📨 Is group ke ${inReview} rows pehle se main staff ko bheje hue hain — review ka wait karo`);
      } else {
        toast.error('Is group me submit karne ke liye koi row nahi hai');
      }
      return;
    }
    const isSubStaff = user?.role === 'sub_staff';
    const verb = isSubStaff ? 'main staff ko bhejein' : 'lock kar diya jaaye';
    if (!window.confirm(`Group #${gIdx + 1} ke ${eligibleIds.length} rows ${verb}?`)) return;
    try {
      const payload = {
        row_ids: eligibleIds,
        account_id: opts.accountId || null,
        card_id: opts.cardId || null,
        // Item: lock how much account-prebalance vs card debit was used in this group.
        prebalance_used: Number(groupPrebalance[gIdx] || 0),
      };
      const { data } = await api.post(`/booking-execution/${fileId}/submit`, payload);
      if (data.is_handoff) {
        toast.success(`📨 Group #${gIdx + 1}: ${data.submitted} rows main staff ko bheje gaye for review`);
      } else {
        toast.success(`✅ Group #${gIdx + 1}: ${data.submitted} rows submitted${data.all_done ? ' · file Completed' : ''}`);
      }
      await reload();
      await fetchAccList();
      await fetchCardList();
      if (data.all_done) {
        // Show report before redirect
        setShowSubmitReport(true);
        setTimeout(() => { window.location.href = '/assigned-files'; }, 5000);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Submit failed'); }
  };

  const exportXlsx = async () => {
    try {
      const r = await api.get(`/booking-execution/${fileId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `Bookings_${fileId}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error('Export failed'); }
  };

  // ── Sub_staff handoff approval (Feb 2026) ──
  // Main staff (or admin/owner) sees count of `pending_review` rows and can
  // bulk-approve them. Approval fires the deferred account/card counters
  // and the card-ledger debit entry.
  const pendingReviewCount = rows.filter(r => r.phase_status === 'pending_review').length;
  const [approving, setApproving] = useState(false);
  const approveAllPending = async () => {
    if (pendingReviewCount === 0) return;
    if (!window.confirm(
      `${pendingReviewCount} pending rows approve karke lock kar diye jayein?\n\n` +
      `Card balance debit aur account daily-counter ab fire honge.`
    )) return;
    setApproving(true);
    try {
      const { data } = await api.post(`/booking-execution/${fileId}/approve-pending`, {});
      toast.success(`✅ ${data.approved} rows approved & locked${data.all_done ? ' · file Completed' : ''}`);
      await reload();
      await fetchAccList();
      await fetchCardList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approve fail');
    } finally {
      setApproving(false);
    }
  };
  const rejectPendingRow = async (rowId) => {
    const note = window.prompt('Reject karne ka reason likho (3+ characters):', '');
    if (!note || note.trim().length < 3) {
      if (note !== null) toast.error('Reason 3+ characters me likho');
      return;
    }
    try {
      const { data } = await api.post(`/booking-execution/${fileId}/reject-pending`, {
        row_ids: [rowId],
        note: note.trim(),
      });
      toast.success(`${data.rejected} row sub-staff ko wapas bhej diya`);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reject fail');
    }
  };

  // ── Account swap (1-by-1 replace with mandatory reason) ──
  // User clicks "Replace Account" on a group → confirmation dialog appears with
  // reason dropdown. On confirm → server returns ONE next available account.
  // Bulk listing is intentionally NOT shown to staff/sub_staff.
  const openSwapDialog = (gIdx, currentAcc) => {
    setSwapDialog({ groupIdx: gIdx, current: currentAcc || null });
    setSwapReason('');
    setSwapExtra('');
  };
  const confirmSwap = async () => {
    if (!swapDialog) return;
    if (!swapReason) { toast.error('Reason select karo — mandatory hai'); return; }
    if (swapReason === 'Other' && swapExtra.trim().length < 3) {
      toast.error("'Other' choose kiya hai → specific reason 3+ characters me likho");
      return;
    }
    setSwapping(true);
    try {
      const { data } = await api.post('/booking-pool/account/swap', {
        current_account_id: swapDialog.current?.id || null,
        reason: swapReason,
        reason_extra: swapExtra.trim(),
        platform: 'amazon',
      });
      const gIdx = swapDialog.groupIdx;
      // Apply new account to this group only — NOT bulk
      setGroupAccounts(prev => ({ ...prev, [gIdx]: data }));
      // Also update top-level `account` if this group's old account was the active one
      if (account?.id === swapDialog.current?.id) setAccount(data);
      toast.success(`Replaced — naya account: ${data.data?.email || data.id}`);
      setSwapDialog(null);
      // Refresh the available account list so allocation badges stay correct
      fetchAccList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Swap fail');
    } finally {
      setSwapping(false);
    }
  };

  // ⚡ Quick Switch — 1-click swap with default reason "Account already used"
  // (most common case). No dialog, no confirmation. Power-user shortcut.
  const quickSwapAccount = async (gIdx, currentAcc) => {
    if (quickSwapping) return;
    setQuickSwapping(gIdx);
    try {
      const { data } = await api.post('/booking-pool/account/swap', {
        current_account_id: currentAcc?.id || null,
        reason: 'Account already used',
        reason_extra: '',
        platform: 'amazon',
      });
      setGroupAccounts(prev => ({ ...prev, [gIdx]: data }));
      if (account?.id === currentAcc?.id) setAccount(data);
      toast.success(`⚡ Quick-switched → ${data.data?.email || data.id}`);
      fetchAccList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Quick switch fail');
    } finally {
      setQuickSwapping(null);
    }
  };

  // ── Add rows manually (paste consumer IDs / full TSV / mobile-only list)
  const handleAddRows = async () => {
    const text = (pasteRows || '').trim();
    if (!text) { toast.error('Pehle consumer IDs / mobile numbers paste karo'); return; }
    // Parse: each line → consumer_id [\t mobile [\t name [\t amount]]]
    // Smart detection: if FIRST token on every line is exactly 10 digits and
    // single-token lines, treat them as MOBILE numbers (consumer_id stays empty,
    // staff fills later). Useful for "phone-list-only" sheets.
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const tokensPerLine = lines.map(l => l.split(/\t|,|\s{2,}/).map(s => s.trim()).filter(Boolean));
    const allMobileOnly = tokensPerLine.every(parts => {
      if (parts.length !== 1) return false;
      const digits = parts[0].replace(/\D/g, '');
      return digits.length === 10;
    });
    const out = [];
    if (allMobileOnly) {
      for (const parts of tokensPerLine) {
        const digits = parts[0].replace(/\D/g, '');
        out.push({
          consumer_id: '',         // staff will fill LPG ID later
          mobile: digits,
          customer_name: '',
          amount: Number(file?.booking_rate) || 0,
        });
      }
    } else {
      for (const parts of tokensPerLine) {
        if (!parts.length) continue;
        // If the first token is a 10-digit number AND there are no further tokens,
        // assume it's a mobile number (not LPG ID).
        const firstDigits = parts[0].replace(/\D/g, '');
        const looksLikeMobile = parts.length === 1 && firstDigits.length === 10;
        out.push({
          consumer_id: looksLikeMobile ? '' : parts[0],
          mobile: looksLikeMobile ? firstDigits : (parts[1] || ''),
          customer_name: parts[2] || '',
          // Item 6: auto-fill amount from file's booking rate (1 cylinder per row).
          // If user pasted an explicit amount, honor it.
          amount: Number(parts[3]) || Number(file?.booking_rate) || 0,
        });
      }
    }
    if (!out.length) { toast.error('Koi valid row nahi mili'); return; }
    try {
      const { data } = await api.post(`/booking-execution/${fileId}/add-rows`, { rows: out });
      const note = allMobileOnly ? ' (Mobile-only mode — LPG ID khali, baad me bharna)' : '';
      toast.success(`${data.created} rows add ho gayi${note}`);
      setPasteRows('');
      setShowAddDialog(false);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Add failed'); }
  };

  // ── Import from admin's HPCL-checked report
  const openImportDialog = async () => {
    setShowImportDialog(true);
    try {
      const { data } = await api.get(`/booking-execution/${fileId}/checked-sources`);
      setCheckedSources(data || []);
    } catch (err) { toast.error('Reports load nahi hue'); }
  };
  const importFromReport = async (reportId) => {
    setImporting(true);
    try {
      const { data } = await api.post(`/booking-execution/${fileId}/import-from-checked/${reportId}`);
      const dup = data.skipped_duplicates ? `, ${data.skipped_duplicates} duplicate skip` : '';
      toast.success(`${data.created} rows imported${dup}`);
      setShowImportDialog(false);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  // ── Re-parse the original Excel to refresh mobile/customer/extras
  const reparseSource = async () => {
    if (!window.confirm('Excel se mobile/customer/extras dobara nikalu? Status & notes preserve rahenge.')) return;
    try {
      const { data } = await api.post(`/booking-execution/${fileId}/reparse-source`);
      toast.success(`${data.updated} rows updated, ${data.inserted} new (total parsed ${data.total_parsed})`);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Re-parse failed'); }
  };

  // ── Bulk selection helpers
  const toggleRow = (rowId) => {
    setSelectedRowIds(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
      return next;
    });
  };
  const toggleAllInGroup = (groupRows) => {
    setSelectedRowIds(prev => {
      const next = new Set(prev);
      const allSelected = groupRows.every(r => next.has(r.id));
      if (allSelected) {
        groupRows.forEach(r => next.delete(r.id));
      } else {
        groupRows.forEach(r => { if (!r.submitted_at) next.add(r.id); });
      }
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedRowIds(prev => {
      const eligible = filtered.filter(r => !r.submitted_at).map(r => r.id);
      if (eligible.every(id => prev.has(id))) return new Set();
      return new Set(eligible);
    });
  };
  const clearSelection = () => setSelectedRowIds(new Set());
  const bulkSetStatus = async (newStatus) => {
    if (selectedRowIds.size === 0) { toast.error('Pehle rows select karo'); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/booking-execution/${fileId}/bulk-status`, {
        row_ids: Array.from(selectedRowIds),
        status: newStatus,
      });
      toast.success(`${data.updated} rows → ${newStatus.toUpperCase()}`);
      clearSelection();
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk update failed'); }
    finally { setBulkBusy(false); }
  };

  // ─────────────── Excel-style keyboard navigation ───────────────
  // Cell id pattern: `nav-{rowId}-{colKey}` (set as data-nav-id on input/select)
  // Order of editable columns in Sheet View tbody (matches table layout)
  const NAV_COLS = ['consumer_id', 'mobile', 'customer_name', 'amount', 'status', 'notes'];
  const focusCell = (rowId, colKey) => {
    if (!rowId || !colKey) return false;
    const el = document.querySelector(`[data-nav-id="nav-${rowId}-${colKey}"]`);
    if (!el) return false;
    el.focus();
    if (typeof el.select === 'function') el.select();
    return true;
  };
  const handleCellKeyDown = (e, rowId, colKey) => {
    const filteredVisible = filtered;
    const idxInList = filteredVisible.findIndex(x => x.id === rowId);
    const colIdx = NAV_COLS.indexOf(colKey);
    if (idxInList === -1 || colIdx === -1) return;

    if (e.key === 'Enter' && !e.shiftKey) {
      // Same column, next row
      const next = filteredVisible[idxInList + 1];
      if (next) { e.preventDefault(); focusCell(next.id, colKey); }
    } else if (e.key === 'Enter' && e.shiftKey) {
      // Same column, prev row
      const prev = filteredVisible[idxInList - 1];
      if (prev) { e.preventDefault(); focusCell(prev.id, colKey); }
    } else if (e.key === 'ArrowDown' && (e.target.tagName === 'SELECT' || e.altKey)) {
      const next = filteredVisible[idxInList + 1];
      if (next) { e.preventDefault(); focusCell(next.id, colKey); }
    } else if (e.key === 'ArrowUp' && (e.target.tagName === 'SELECT' || e.altKey)) {
      const prev = filteredVisible[idxInList - 1];
      if (prev) { e.preventDefault(); focusCell(prev.id, colKey); }
    } else if ((e.ctrlKey || e.metaKey) && (e.key === 'd' || e.key === 'D')) {
      // Copy down — fill from previous row's same column
      e.preventDefault();
      const prev = filteredVisible[idxInList - 1];
      if (!prev) { toast.message('Pehli row me Ctrl+D nahi chalega'); return; }
      const valToCopy = prev[colKey] ?? '';
      updateRow(rowId, { [colKey]: valToCopy });
      toast.success(`Copied ${colKey}: "${valToCopy}" from row above`);
    } else if (e.key === 'Escape') {
      e.target.blur();
    }
  };

  // Page-level: Ctrl+S → Submit Final
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        submit();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId]);

  const counts = useMemo(() => rows.reduce((acc, r) => {
    acc.total += 1; acc[r.status] = (acc[r.status] || 0) + 1; acc.amount += Number(r.amount || 0);
    return acc;
  }, { total: 0, success: 0, pending: 0, failed: 0, amount: 0 }), [rows]);

  // Smart search — auto-detects intent based on input pattern
  const searchInfo = useMemo(() => {
    const q = (search || '').trim();
    if (!q) return { mode: 'all', q: '' };
    const digits = q.replace(/\D/g, '');
    if (q === digits && digits.length >= 13) return { mode: 'consumer', q: digits };
    if (q === digits && digits.length === 10) return { mode: 'mobile', q: digits };
    if (q === digits && digits.length >= 4 && digits.length <= 9) return { mode: 'mobile-prefix', q: digits };
    return { mode: 'text', q: q.toLowerCase() };
  }, [search]);

  const filtered = useMemo(() => rows.filter(r => {
    if (filter !== 'all' && r.status !== filter) return false;
    if (!searchInfo.q) return true;
    const cid = (r.consumer_id || '').replace(/\D/g, '');
    const mob = (r.mobile || '').replace(/\D/g, '');
    if (searchInfo.mode === 'consumer') return cid.includes(searchInfo.q);
    if (searchInfo.mode === 'mobile') return mob === searchInfo.q || mob.endsWith(searchInfo.q);
    if (searchInfo.mode === 'mobile-prefix') return mob.includes(searchInfo.q);
    // text mode — search across consumer/mobile/name AND extras
    const haystack = `${r.consumer_id || ''} ${r.customer_name || ''} ${r.mobile || ''} ${Object.values(r.extras || {}).join(' ')}`.toLowerCase();
    return haystack.includes(searchInfo.q);
  }), [rows, filter, searchInfo]);

  const accData = account?.data || {};
  // Smart key detection — handle typos (Acount, Pasword, OTP Cod) + standard names.
  // findKey: tries each pattern in order, returns the first key whose normalized
  // form CONTAINS that pattern. Patterns should be ordered most-specific FIRST.
  const findKey = (data, ...patterns) => {
    const keys = Object.keys(data || {});
    for (const p of patterns) {
      const k = keys.find(k => k.toLowerCase().replace(/[^a-z0-9]/g, '').includes(p));
      if (k) return k;
    }
    return null;
  };
  // Helper: avoid matching admin-only fields (Linked Gmail, Recovery Email, Created By)
  // when picking primary email/password. These should appear ONLY in extras section.
  const isAdminOnlyKey = (k) => {
    const kl = (k || '').toLowerCase().replace(/\s/g, '');
    return /(linkedgmail|recoveryemail|createdby)/i.test(kl);
  };
  // Find ALL password keys — Amazon 2FA upload stores `Pasword` (numeric pin like 778899)
  // AND `Pasword2` (alphanumeric like Patna@123). Both should be visible+merged with OTP.
  // Skip "Linked Gmail Password" — admin-only.
  const allPwKeys = Object.keys(accData).filter(k => {
    if (isAdminOnlyKey(k)) return false;
    const kl = k.toLowerCase().replace(/[^a-z0-9]/g, '');
    return kl.includes('password') || kl.includes('pasword') || kl.includes('pswd');
  });
  // Email: prioritize Acount/Account before generic 'email' substring (avoids
  // Recovery Email / Linked Gmail being picked as primary email).
  const emailCandidates = Object.keys(accData).filter(k => !isAdminOnlyKey(k));
  const emailDataSubset = Object.fromEntries(emailCandidates.map(k => [k, accData[k]]));
  let accEmailKey = findKey(emailDataSubset, 'acount', 'account', 'email');
  let accPwKey = allPwKeys[0] || null;
  let accPw2Key = allPwKeys[1] || null;
  let accMobKey = findKey(accData, 'mobile', 'phone', 'contact', 'number');
  let accOtpKey = findKey(accData, 'totpsecret', 'otpcod', 'otpcode', 'totp', 'otp', 'secret');
  // ── Value-pattern fallback: when keys themselves look like values
  // (e.g., live-env parser stored `data["RPVWENEJ1@AMREXY.COM"] = "..."`),
  // findKey returns null. Detect role from VALUE shape instead. ──
  const _isEmail = (v) => /^[\w.+-]+@[\w-]+\.[\w.-]+$/i.test(String(v || '').trim());
  const _isMobile = (v) => /^(\+?91)?[6-9]\d{9}$/.test(String(v || '').replace(/\s|-/g, ''));
  const _isPin = (v) => /^\d{4,8}$/.test(String(v || '').trim());
  const _isBase32 = (v) => {
    const s = String(v || '').replace(/\s/g, '').toUpperCase();
    return s.length >= 16 && /^[A-Z2-7=]+$/.test(s);
  };
  const _allKeys = Object.keys(accData).filter(k => !isAdminOnlyKey(k));
  const _findByValue = (test, taken = new Set()) => _allKeys.find(k => !taken.has(k) && test(accData[k]));
  const _claimed = new Set();
  if (accEmailKey) _claimed.add(accEmailKey);
  if (accPwKey) _claimed.add(accPwKey);
  if (accPw2Key) _claimed.add(accPw2Key);
  if (accMobKey) _claimed.add(accMobKey);
  if (accOtpKey) _claimed.add(accOtpKey);
  if (!accEmailKey) {
    // Try VALUE first, then KEY
    accEmailKey = _findByValue(_isEmail, _claimed) || _allKeys.find(k => !_claimed.has(k) && _isEmail(k));
    if (accEmailKey) _claimed.add(accEmailKey);
  }
  if (!accMobKey) {
    accMobKey = _findByValue(_isMobile, _claimed) || _allKeys.find(k => !_claimed.has(k) && _isMobile(k));
    if (accMobKey) _claimed.add(accMobKey);
  }
  if (!accOtpKey) {
    accOtpKey = _findByValue(_isBase32, _claimed) || _allKeys.find(k => !_claimed.has(k) && _isBase32(k));
    if (accOtpKey) _claimed.add(accOtpKey);
  }
  if (!accPwKey) {
    accPwKey = _findByValue(_isPin, _claimed) || _allKeys.find(k => !_claimed.has(k) && _isPin(k));
    if (accPwKey) _claimed.add(accPwKey);
  }
  if (!accPw2Key) {
    // Pasword2 = any remaining alphanumeric value 4-30 chars (not email/mobile/totp)
    accPw2Key = _allKeys.find(k => !_claimed.has(k) && {
      v: accData[k],
    } && typeof accData[k] === 'string' && accData[k].length >= 4 && accData[k].length <= 30 && !_isEmail(accData[k]) && !_isMobile(accData[k]) && !_isBase32(accData[k]));
  }
  // Helper: get the actual displayable VALUE for a chosen key.
  // If the KEY itself looks like the value-type (live-env weirdness where
  // key=email/password/totp) and the dict entry's value is also a similar
  // string, prefer the longer/more-specific one. Else just return accData[k].
  const _resolveVal = (k, classifier) => {
    if (!k) return '';
    const v = accData[k];
    const keyLooks = classifier(k);
    const valLooks = classifier(v);
    if (keyLooks && !valLooks) return k;          // value isn't this type → key is the data
    if (!keyLooks && valLooks) return v || '';    // standard case
    if (keyLooks && valLooks) return v || k;      // both look like — prefer value
    return v || '';
  };
  const accEmailVal = _resolveVal(accEmailKey, _isEmail);
  const accMobVal = _resolveVal(accMobKey, _isMobile);
  const accPwVal = _resolveVal(accPwKey, _isPin);
  const accPw2Val = accPw2Key ? (accData[accPw2Key] || accPw2Key) : '';
  const accSecret = (accOtpKey ? (_resolveVal(accOtpKey, _isBase32) || accOtpKey) : '') || '';
  // Any remaining keys to show as extra fields (limit to 3 to avoid clutter).
  // EXCLUDE TOTP secret + both passwords + email/mobile since they're already in main row.
  const knownKeys = new Set([accEmailKey, accPwKey, accPw2Key, accMobKey, accOtpKey].filter(Boolean));
  const extraKeys = Object.keys(accData).filter(k => !knownKeys.has(k)).slice(0, 3);

  return (
    <div className="space-y-3" data-testid="booking-workspace">
      {/* Header */}
      <div className="bg-white border rounded p-3 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs text-gray-500">{file?.distributor_name} · {file?.oil_company?.toUpperCase()}</div>
          <div className="font-bold text-lg">{file?.original_filename}</div>
          {file?.booking_rate > 0 && (
            <div className="mt-1 inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-full px-3 py-1" data-testid="ws-rate-pill">
              💰 Booking Rate: ₹{file.booking_rate}/cyl
              {file?.booking_agency && <span className="text-emerald-600/80 font-normal">· {file.booking_agency}</span>}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Item Feb 2026: Auto-Distribute Cards — hidden for sub_staff (admin/main-staff only). */}
          {user?.role !== 'sub_staff' && file?.booking_rate > 0 && rows.filter(r => !r.submitted_at).length > 0 && (
            <Button
              variant="outline"
              onClick={() => setShowAutoDistribute(true)}
              className="gap-1 border-violet-300 text-violet-700 hover:bg-violet-50"
              data-testid="ws-auto-distribute-btn"
              title="Auto-assign cards based on booking rate, group size, and pre-balance"
            >
              ⚡ Auto-Distribute Cards
            </Button>
          )}
          <Button variant="outline" onClick={() => setShowAddDialog(true)} className="gap-1" data-testid="ws-add-rows-btn">
            <Plus size={16} /> Add Rows
          </Button>
          {/* Item Feb 2026: hide Admin's Checked File / Re-parse Excel for sub_staff */}
          {user?.role !== 'sub_staff' && (
            <Button variant="outline" onClick={openImportDialog} className="gap-1 border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="ws-import-checked-btn" title="Admin ne HPCL Data Matcher me jo file check ki hai, usse consumer IDs import karo">
              <DownloadSimple size={16} /> Admin's Checked File
            </Button>
          )}
          {user?.role !== 'sub_staff' && (
            <Button variant="outline" onClick={reparseSource} className="gap-1 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="ws-reparse-btn" title="Excel file se mobile/customer/extras dobara nikalo (status preserve hoga)">
              <ArrowRight size={16} /> Re-parse Excel
            </Button>
          )}
          {/* Export Excel: 
              - admin/owner/main-staff → always visible
              - sub_staff → only visible AFTER they have submitted (handoff to main staff).
                We detect this by any row reaching phase_status pending_review or submitted. */}
          {(user?.role !== 'sub_staff' || rows.some(r => r.phase_status === 'pending_review' || r.phase_status === 'submitted')) && (
            <Button variant="outline" onClick={exportXlsx} className="gap-1" data-testid="ws-export-xlsx-btn"><FileXls size={16} /> Export Excel</Button>
          )}
          {/* Card-chunk summary pill — Smart Card Row Split (Feb 2026). Shows
              available/used chunks when staff chose chunk-split during distribute. */}
          {chunkSummary && chunkSummary.total > 0 && (
            <div
              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-violet-50 border-2 border-violet-300 text-[11px] font-bold text-violet-900"
              data-testid="chunk-summary-pill"
              title={`Total: ${chunkSummary.total} · Available: ${chunkSummary.available} · Used: ${chunkSummary.used} · ₹ available: ${chunkSummary.available_amount.toLocaleString('en-IN')}`}
            >
              📦 Chunks: <span className="text-emerald-700">{chunkSummary.available}</span>/{chunkSummary.total} avail
              <span className="text-gray-500">· ₹{Math.round(chunkSummary.available_amount).toLocaleString('en-IN')}</span>
            </div>
          )}
          {/* Pending-review approval button — shown to staff/admin/owner when sub_staff
              has handed off rows for review */}
          {(!isStaffOrSub || user?.role === 'staff') && pendingReviewCount > 0 && (
            <Button
              onClick={approveAllPending}
              disabled={approving}
              className="bg-amber-600 hover:bg-amber-700 gap-1 text-white"
              data-testid="ws-approve-pending-btn"
              title={`${pendingReviewCount} sub-staff submissions awaiting approval`}
            >
              ✓ {approving ? 'Approving…' : `Approve ${pendingReviewCount} Pending`}
            </Button>
          )}
          <Button onClick={submit} disabled={submitting || rows.length === 0} className="bg-green-600 hover:bg-green-700 gap-1" data-testid="ws-submit-final-btn">
            <CheckCircle size={16} />
            {submitting
              ? 'Submitting…'
              : (isStaffOrSub && user?.role === 'sub_staff' ? 'Send to Main Staff' : 'Submit Final')}
          </Button>
        </div>
      </div>

      {/* Resume Banner — auto-shown when user has previously submitted some rows */}
      {resumeInfo && (
        <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border-2 border-amber-300 rounded-lg p-3 flex items-center gap-3 shadow-sm" data-testid="resume-banner">
          <div className="shrink-0 w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center text-lg font-bold animate-pulse">
            ▶
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-amber-900">
              Resume from row #{resumeInfo.humanIndex} — {resumeInfo.submitted}/{resumeInfo.total} done
            </div>
            <div className="text-[11px] text-amber-700 truncate">
              Pichli baar yahan rukke the:&nbsp;
              <span className="font-mono font-semibold">{resumeInfo.consumer || '—'}</span>
              {resumeInfo.customerName ? <span className="text-amber-600"> · {resumeInfo.customerName}</span> : null}
            </div>
          </div>
          <Button
            size="sm"
            onClick={scrollToResume}
            className="bg-amber-500 hover:bg-amber-600 text-white gap-1 shrink-0"
            data-testid="resume-scroll-btn"
          >
            <ArrowRight size={14} weight="bold" /> Jump to Row
          </Button>
        </div>
      )}

      {/* Active Account Widget — HIDDEN per user request (Feb 2026)
          Account info now shown ONLY alongside group rows below.
          Kept as `hidden` so any internal references / state still mount silently. */}
      <div className="bg-white border-2 border-blue-200 rounded p-3 hidden" data-testid="active-account-widget">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-bold text-blue-700 uppercase">🔐 Active Account</div>
          <Button size="sm" variant="outline" onClick={() => { setShowAccPicker(true); fetchAccList(); }} className="text-xs h-7 gap-1">
            <MagnifyingGlass size={12} /> Switch / Search
          </Button>
        </div>
        {account ? (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-sm">
            <div onClick={() => copy(accEmailVal, accEmailKey || 'Email')} className="cursor-pointer hover:bg-blue-50 p-1 rounded" data-testid="acc-email-cell">
              <div className="text-[10px] text-gray-500 uppercase truncate">{accEmailKey || 'Email'}</div>
              <div className="font-mono text-xs truncate">{accEmailVal || '-'}  <Copy size={10} className="inline text-gray-400" /></div>
            </div>
            <div onClick={() => copy(accMobVal, accMobKey || 'Mobile')} className="cursor-pointer hover:bg-blue-50 p-1 rounded" data-testid="acc-mobile-cell">
              <div className="text-[10px] text-gray-500 uppercase truncate">{accMobKey || 'Mobile'}</div>
              <div className="font-mono text-xs">{accMobVal || '-'}  <Copy size={10} className="inline text-gray-400" /></div>
            </div>
            <div onClick={() => copy(accPwVal, accPwKey || 'Password')} className={`cursor-pointer hover:bg-blue-50 p-1 rounded ${isStaffOrSub ? 'hidden' : ''}`} data-testid="acc-pwd-cell">
              <div className="text-[10px] text-gray-500 uppercase truncate">{accPwKey || 'Password'}</div>
              <div className="font-mono text-xs">{accPwVal || '-'}  <Copy size={10} className="inline text-gray-400" /></div>
            </div>
            {accPw2Key ? (
              <div onClick={() => copy(accPw2Val, accPw2Key)} className={`cursor-pointer hover:bg-blue-50 p-1 rounded ${isStaffOrSub ? 'hidden' : ''}`} data-testid="acc-pwd2-cell">
                <div className="text-[10px] text-gray-500 uppercase truncate">{accPw2Key}</div>
                <div className="font-mono text-xs break-all">{accPw2Val || '-'}  <Copy size={10} className="inline text-gray-400" /></div>
              </div>
            ) : (
              <div className={`p-1 ${isStaffOrSub ? 'hidden' : ''}`}>
                <div className="text-[10px] text-gray-500 uppercase truncate">{accOtpKey || 'TOTP'}</div>
                <TOTPWidget secret={accSecret} label={accOtpKey || 'TOTP'} />
              </div>
            )}
            {accPw2Key && (
              <div className={`p-1 ${isStaffOrSub ? 'hidden' : ''}`}>
                <div className="text-[10px] text-gray-500 uppercase truncate">{accOtpKey || 'TOTP'}</div>
                <TOTPWidget secret={accSecret} label={accOtpKey || 'TOTP'} />
              </div>
            )}
            {/* Combined Login Password (password + live OTP) — one-click direct login.
                Two widgets when both Pasword + Pasword2 exist (numeric PIN + email password). */}
            <div className="p-1">
              <div className="text-[10px] text-gray-500 uppercase truncate">🔑 Login Password</div>
              <div className="space-y-1">
                {accPwKey && (
                  <LoginPasswordWidget
                    password={accPwVal}
                    secret={accSecret}
                    label={`${accPwKey} + OTP`}
                    testId="active-acc-login-password"
                  />
                )}
                {accPw2Key && (
                  <LoginPasswordWidget
                    password={accPw2Val}
                    secret={accSecret}
                    label={`${accPw2Key} + OTP`}
                    testId="active-acc-login-password-2"
                  />
                )}
              </div>
            </div>
            <div className="p-1 text-right">
              <div className="text-[10px] text-gray-500">Used Today</div>
              <div className="font-bold">{account.daily_usage_count}/{account.daily_limit}</div>
              {account.platform && <div className="text-[10px] text-blue-600 uppercase">{account.platform}</div>}
              {account.in_cooldown && <div className="text-[10px] text-red-600">🔒 Cooldown</div>}
            </div>
            {extraKeys.map((k) => (
              <div key={k} onClick={() => copy(accData[k], k)} className="cursor-pointer hover:bg-blue-50 p-1 rounded col-span-2 md:col-span-6 border-t pt-2 mt-1" data-testid={`acc-extra-${k}`}>
                <div className="text-[10px] text-gray-500 uppercase truncate">{k}</div>
                <div className="font-mono text-[11px] break-all">{accData[k] || '-'}  <Copy size={10} className="inline text-gray-400" /></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-amber-700">Koi available account nahi hai. <button onClick={() => { setShowAccPicker(true); fetchAccList(); }} className="underline">Search ya add karo</button>.</div>
        )}
      </div>

      {/* Active Card Widget — HIDDEN per user request (Feb 2026)
          Card info now shown ONLY in each group header below. */}
      <div className="bg-white border-2 border-purple-200 rounded p-3 hidden">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-bold text-purple-700 uppercase">💳 Active Card</div>
          <Button size="sm" variant="outline" onClick={() => { setShowCardPicker(true); fetchCardList(); }} className="text-xs h-7 gap-1">
            <MagnifyingGlass size={12} /> Switch / Search
          </Button>
        </div>
        {card ? (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-sm">
            <div onClick={() => copy(card.card_number, 'Card')} className="cursor-pointer hover:bg-purple-50 p-1 rounded">
              <div className="text-[10px] text-gray-500">Card</div>
              <div className="font-mono text-xs">****{card.last4}  <Copy size={10} className="inline text-gray-400" /></div>
            </div>
            <div onClick={() => copy(card.expiry, 'Expiry')} className="cursor-pointer hover:bg-purple-50 p-1 rounded">
              <div className="text-[10px] text-gray-500">Expiry</div>
              <div className="font-mono text-xs">{card.expiry || '-'}  <Copy size={10} className="inline text-gray-400" /></div>
            </div>
            <div onClick={() => copy(card.cvv, 'CVV')} className="cursor-pointer hover:bg-purple-50 p-1 rounded">
              <div className="text-[10px] text-gray-500">CVV</div>
              <div className="font-mono text-xs">{card.cvv ? '●●●' : '-'}  <Copy size={10} className="inline text-gray-400" /></div>
            </div>
            <div className="p-1">
              <div className="text-[10px] text-gray-500">Balance</div>
              <div className={`font-bold text-xs ${Number(card.available_limit || 0) > 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="card-balance">
                {fmt(card.available_limit)}
              </div>
              {card.initial_limit > 0 && card.initial_limit !== card.available_limit && (
                <div className="text-[9px] text-gray-500">used {fmt(card.used_amount || 0)} / {fmt(card.initial_limit)}</div>
              )}
            </div>
            <div className="p-1 text-right">
              <div className="text-[10px] text-gray-500">Used Today</div>
              <div className="font-bold">{card.daily_usage_count}/{card.daily_limit}</div>
              {card.in_cooldown && <div className="text-[10px] text-red-600">🔒 Cooldown</div>}
              {card.linked_agency_name && <div className="text-[9px] text-violet-700 bg-violet-50 px-1 rounded inline-block">🔒 {card.linked_agency_name}</div>}
            </div>
            {card.card_password && (
              <div onClick={() => copy(card.card_password, 'Card Password')} className="cursor-pointer hover:bg-purple-50 p-1 rounded col-span-2 md:col-span-5 border-t pt-2 mt-1" data-testid="card-password-cell">
                <div className="text-[10px] text-gray-500 uppercase truncate">🔑 Card Login Password</div>
                <div className="font-mono text-xs">{card.card_password}  <Copy size={10} className="inline text-gray-400" /></div>
              </div>
            )}
          </div>
        ) : <div className="text-sm text-amber-700">Koi card assigned nahi hai.</div>}
      </div>

      {/* Progress + Filters + View Mode */}
      <div className={`border rounded p-2 flex items-center justify-between flex-wrap gap-2 text-xs ${amazonLockEnabled ? 'bg-emerald-50 border-emerald-300' : 'bg-blue-50 border-blue-300'}`} data-testid="ws-mode-banner">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded font-bold ${amazonLockEnabled ? 'bg-emerald-600 text-white' : 'bg-blue-600 text-white'}`}>
            {amazonLockEnabled ? '🟢 AMAZON MODE' : '🔵 NORMAL BOOKING MODE'}
          </span>
          <span className="text-gray-700">
            {amazonLockEnabled
              ? '5/5 group view active — Amazon account + card credentials per group.'
              : 'Google-sheet flat view — har row ka card details visible. Amazon paused hai.'}
          </span>
        </div>
        {(user?.role === 'owner' || user?.role === 'admin') && (
          <a
            href="/account-stock"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] underline text-gray-700 hover:text-gray-900"
            data-testid="ws-mode-toggle-link"
          >
            {amazonLockEnabled ? 'Pause Amazon →' : 'Resume Amazon →'}
          </a>
        )}
      </div>

      {/* Default Booking Platform top bar — auto-applies to all draft rows.
          Per-row override lives in each row (orange border indicator). */}
      <div className="border rounded p-2 flex items-center justify-between flex-wrap gap-2 bg-indigo-50 border-indigo-300" data-testid="default-platform-bar">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-indigo-900">📱 Default Platform:</span>
          <select
            value={defaultPlatform}
            onChange={e => setDefaultPlatform(e.target.value)}
            className="h-7 text-xs border border-indigo-400 rounded px-2 bg-white font-medium"
            data-testid="default-platform-select"
          >
            {PLATFORM_OPTIONS.map(p => (
              <option key={p} value={p}>
                {p === 'TataNeu' ? '📱 TataNeu (+convenience fee)' :
                 p === 'JioFinance' ? '📱 JioFinance (+convenience fee)' :
                 '💵 Normal (no extra fee)'}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700"
            onClick={async () => {
              const res = await applyDefaultPlatformToAll(defaultPlatform, { force: true });
              toast.success(`${defaultPlatform} applied to ${res.applied} rows`);
            }}
            disabled={applyingPlatform}
            data-testid="apply-platform-all-btn"
          >
            {applyingPlatform ? '…' : 'Apply to all rows'}
          </Button>
        </div>
        <span className="text-[10px] text-gray-600">
          🟠 Orange border = row has manual platform override (different from default).
        </span>
      </div>

      <div className="bg-white border rounded p-3">
        <div className="flex items-center gap-3 flex-wrap mb-2">
          <span className="text-xs">✅ <b>{counts.success}</b> Success</span>
          <span className="text-xs">🟡 <b>{counts.pending}</b> Pending</span>
          <span className="text-xs">🔴 <b>{counts.failed}</b> Failed</span>
          <span className="text-xs ml-auto">{counts.total} total · {fmt(counts.amount)}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div className="h-full bg-green-500 transition-all" style={{ width: `${counts.total ? (counts.success / counts.total) * 100 : 0}%` }} />
        </div>
        <div className="flex gap-1 mt-2 flex-wrap items-center">
          {['all', 'success', 'pending', 'failed'].map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`text-[10px] px-2 py-0.5 rounded border ${filter === f ? 'bg-blue-600 text-white border-blue-700' : 'bg-white text-gray-700 border-gray-300'}`}>
              {f.toUpperCase()} {f !== 'all' && counts[f] > 0 ? `(${counts[f]})` : ''}
            </button>
          ))}
          <div className="relative flex-1 min-w-[180px]" data-testid="ws-search-wrap">
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search: 17-digit LPG ID · 10-digit Mobile · Name · anything…"
              className="h-7 text-xs pr-20"
              data-testid="ws-search-input"
            />
            {searchInfo.mode !== 'all' && (
              <span
                className={`absolute right-1 top-1/2 -translate-y-1/2 text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
                  searchInfo.mode === 'consumer' ? 'bg-violet-100 text-violet-700' :
                  searchInfo.mode === 'mobile' ? 'bg-emerald-100 text-emerald-700' :
                  searchInfo.mode === 'mobile-prefix' ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-700'
                }`}
                title={
                  searchInfo.mode === 'consumer' ? 'Searching by Consumer/LPG ID (digits ≥13)' :
                  searchInfo.mode === 'mobile' ? 'Searching by exact Mobile (10 digits)' :
                  searchInfo.mode === 'mobile-prefix' ? 'Searching Mobile prefix' :
                  'Searching all fields'
                }
                data-testid="ws-search-mode-badge"
              >
                {searchInfo.mode === 'consumer' ? 'LPG ID' :
                 searchInfo.mode === 'mobile' ? 'MOBILE' :
                 searchInfo.mode === 'mobile-prefix' ? 'MOBILE…' :
                 'TEXT'}
              </span>
            )}
          </div>
          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 ml-1 border rounded p-0.5 bg-gray-50">
            <button onClick={() => setViewMode('sheet')} className={`text-[10px] px-2 py-0.5 rounded ${viewMode === 'sheet' ? 'bg-violet-600 text-white' : 'text-gray-600'}`} data-testid="view-sheet-btn">📊 Sheet View</button>
            <button onClick={() => setViewMode('list')} className={`text-[10px] px-2 py-0.5 rounded ${viewMode === 'list' ? 'bg-violet-600 text-white' : 'text-gray-600'}`} data-testid="view-list-btn">📋 List</button>
          </div>
          {viewMode === 'sheet' && (
            <>
              <span className="text-[10px] text-gray-500 ml-1">per card:</span>
              <select value={groupSize} onChange={e => setGroupSize(Number(e.target.value))} className="h-7 text-[10px] border rounded px-1" data-testid="group-size-select">
                {Array.from({ length: 22 }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <label className="inline-flex items-center gap-1 ml-2 text-[10px] cursor-pointer select-none bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5" data-testid="hide-submitted-toggle">
                <input type="checkbox" checked={hideSubmittedGroups} onChange={(e) => setHideSubmittedGroups(e.target.checked)} className="accent-emerald-600" />
                <span className="font-bold">Hide submitted groups</span>
              </label>
              <span
                className="text-[10px] text-violet-600 ml-1 hidden md:inline cursor-pointer hover:underline"
                title='Click or press "?" to see all shortcuts'
                onClick={() => {
                  // dispatch a synthetic "?" keydown to toggle the overlay
                  const ev = new KeyboardEvent('keydown', { key: '?', bubbles: true });
                  window.dispatchEvent(ev);
                }}
                data-testid="kb-shortcuts-hint"
              >
                ⌨ <span className="underline decoration-dotted">shortcuts</span>
              </span>
            </>
          )}
        </div>
      </div>

      {/* Sticky Bulk Action bar — shows when 1+ rows selected */}
      {selectedRowIds.size > 0 && (
        <div className="sticky top-2 z-30 bg-violet-600 text-white rounded-lg shadow-xl px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid="bulk-action-bar">
          <span className="font-bold text-sm">
            {selectedRowIds.size} selected
          </span>
          <span className="text-[11px] text-violet-100 hidden sm:inline">·</span>
          <button
            type="button"
            onClick={toggleSelectAll}
            className="text-[11px] underline decoration-dotted hover:text-white text-violet-100"
            data-testid="bulk-select-all"
          >
            Select all visible ({filtered.filter(r => !r.submitted_at).length})
          </button>
          <span className="ml-auto flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] text-violet-200">Mark as:</span>
            <Button size="sm" disabled={bulkBusy} onClick={() => bulkSetStatus('success')} className="h-7 px-3 bg-green-500 hover:bg-green-600 gap-1 text-[11px]" data-testid="bulk-mark-success">
              ✓ Success
            </Button>
            <Button size="sm" disabled={bulkBusy} onClick={() => bulkSetStatus('pending')} className="h-7 px-3 bg-amber-500 hover:bg-amber-600 gap-1 text-[11px]" data-testid="bulk-mark-pending">
              ⏳ Pending
            </Button>
            <Button size="sm" disabled={bulkBusy} onClick={() => bulkSetStatus('failed')} className="h-7 px-3 bg-red-500 hover:bg-red-600 gap-1 text-[11px]" data-testid="bulk-mark-failed">
              ✗ Failed
            </Button>
            <Button size="sm" variant="ghost" onClick={clearSelection} className="h-7 px-2 text-[11px] text-violet-100 hover:text-white hover:bg-violet-700" data-testid="bulk-clear">
              Clear
            </Button>
          </span>
        </div>
      )}

      {/* Booking Sheet — List View */}
      {/* Booking Sheet — Normal Booking Mode (Google Sheet style, grouped by card) */}
      {viewMode === 'list' && (() => {
        // Build a lookup of card-id → card object (number, exp, cvv, password)
        const cardLookup = new Map();
        (cardChunks || []).forEach(ch => {
          const cid = ch.card_id || ch.id;
          if (cid && ch.card_number) cardLookup.set(cid, ch);
        });
        (cardList || []).forEach(c => {
          if (c?.id && !cardLookup.has(c.id)) cardLookup.set(c.id, c);
        });

        // Group filtered rows by card_id, preserving original order
        const cardGroups = [];
        const cardGroupIdx = new Map();
        filtered.forEach(r => {
          const key = r.card_id || '__unassigned__';
          if (!cardGroupIdx.has(key)) {
            cardGroupIdx.set(key, cardGroups.length);
            cardGroups.push({ cardId: r.card_id || null, card: r.card_id ? cardLookup.get(r.card_id) : null, rows: [] });
          }
          cardGroups[cardGroupIdx.get(key)].rows.push(r);
        });

        // Also surface ALL of staff's available cards even if no rows are
        // assigned to them yet — so the inventory is visible at a glance.
        // Skip cards already grouped above.
        (cardList || []).forEach(c => {
          if (!c?.id) return;
          if (cardGroupIdx.has(c.id)) return;
          cardGroups.push({ cardId: c.id, card: c, rows: [] });
        });

        return (
          <div className="space-y-3" data-testid="normal-booking-grid" style={{ fontSize: `${zoom}%` }}>
            {/* Zoom toolbar (Google Sheets style) */}
            <div className="flex items-center justify-end gap-1 text-[11px] text-gray-600">
              <span className="mr-1">Zoom:</span>
              {[75, 100, 125, 150].map(z => (
                <button
                  key={z}
                  type="button"
                  onClick={() => setZoom(z)}
                  className={`px-2 py-0.5 rounded border ${zoom === z ? 'bg-blue-600 text-white border-blue-700' : 'bg-white border-gray-300 hover:bg-gray-50'}`}
                  data-testid={`zoom-${z}`}
                >{z}%</button>
              ))}
            </div>
            {loading && <div className="text-center text-gray-400 py-6 bg-white border rounded">Loading…</div>}
            {!loading && filtered.length === 0 && (
              <div className="text-center text-gray-400 py-6 bg-white border rounded">
                {rows.length === 0 ? 'Koi row nahi — Staff ne abhi distribute nahi kiya' : 'No match'}
              </div>
            )}
            {!loading && cardGroups.map((cg, cgIdx) => {
              const cd = cg.card;
              const groupTotal = cg.rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
              const submitted = cg.rows.filter(r => r.submitted_at).length;
              const label = cd ? `****${cd.last4 || (cd.card_number || '').slice(-4)}` : 'No card assigned';
              return (
                <div key={cg.cardId || `unassigned-${cgIdx}`} className="border-2 border-blue-200 rounded-lg overflow-hidden bg-white" data-testid={`card-group-${cgIdx}`}>
                  {/* Card header — full credentials, click-to-copy chips */}
                  <div className="px-3 py-2 bg-blue-50 border-b border-blue-200 flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-bold text-blue-900 text-sm">💳 Card #{cgIdx + 1}</span>
                    {cd ? (
                      <>
                        <button type="button" onClick={() => copy(cd.card_number, 'Card')} className="px-2 py-1 rounded bg-white border-2 border-blue-300 hover:bg-blue-100 inline-flex items-center gap-1 font-mono font-bold text-blue-900" data-testid={`card-group-${cgIdx}-no`}>
                          {label} <Copy size={10} className="text-blue-500" />
                        </button>
                        {cd.card_holder && <span className="text-gray-700">👤 {cd.card_holder}</span>}
                        {cd.expiry && (
                          <button type="button" onClick={() => copy(cd.expiry, 'Expiry')} className="px-2 py-1 rounded bg-white border border-amber-300 hover:bg-amber-50 inline-flex items-center gap-1 font-mono" data-testid={`card-group-${cgIdx}-exp`}>
                            Exp {cd.expiry}<Copy size={9} className="text-amber-500" />
                          </button>
                        )}
                        {cd.cvv && (
                          <button type="button" onClick={() => copy(cd.cvv, 'CVV')} className="px-2 py-1 rounded bg-white border border-rose-300 hover:bg-rose-50 inline-flex items-center gap-1 font-mono" data-testid={`card-group-${cgIdx}-cvv`}>
                            CVV ●●●<Copy size={9} className="text-rose-500" />
                          </button>
                        )}
                        {cd.card_password && (
                          <button type="button" onClick={() => copy(cd.card_password, 'Card Pwd')} className="px-2 py-1 rounded bg-white border border-purple-300 hover:bg-purple-50 inline-flex items-center gap-1 font-mono" data-testid={`card-group-${cgIdx}-pwd`}>
                            Pwd {cd.card_password}<Copy size={9} className="text-purple-500" />
                          </button>
                        )}
                        {cd.available_limit != null && (
                          <span className="text-[11px] bg-emerald-100 border border-emerald-300 rounded px-2 py-0.5 text-emerald-900 font-bold">
                            Balance: {fmt(cd.available_limit)}
                          </span>
                        )}
                      </>
                    ) : (
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-amber-800 font-semibold">⚠️ {cg.rows.length} rows ka koi card assigned nahi hai</span>
                        {user?.role !== 'sub_staff' && file?.booking_rate > 0 && (
                          <Button
                            size="sm"
                            onClick={() => setShowAutoDistribute(true)}
                            className="h-7 text-[11px] bg-violet-600 hover:bg-violet-700 font-bold"
                            data-testid="unassigned-cta-auto-distribute"
                          >
                            ⚡ Auto-Distribute Cards Now
                          </Button>
                        )}
                      </div>
                    )}
                    <span className="ml-auto text-[11px] bg-violet-100 border border-violet-300 rounded px-2 py-0.5 text-violet-900 font-bold">
                      {cg.rows.length} rows · {fmt(groupTotal)}{submitted > 0 ? ` · ${submitted} done` : ''}
                    </span>
                  </div>
                  {/* Rows table for this card */}
                  <div className="overflow-x-auto">
                    {cg.rows.length === 0 ? (
                      <div className="text-center text-gray-400 py-4 text-xs italic bg-gray-50 border-t border-gray-200">
                        Is card par koi row assigned nahi hai — Auto-Distribute Cards button se assign karein.
                      </div>
                    ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
                        <tr>
                          <th className="px-2 py-2 w-8 text-center">#</th>
                          <th className="px-2 py-2 text-left w-32">Card</th>
                          <th className="px-2 py-2 text-left">Consumer ID</th>
                          <th className="px-2 py-2 text-left">Mobile</th>
                          <th className="px-2 py-2 text-right">Amount</th>
                          <th className="px-2 py-2 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cg.rows.map((r, i) => {
                          const locked = r.submitted_at;
                          const rowCard = r.card_id ? cardLookup.get(r.card_id) : null;
                          const rowPlatform = (r.extras && r.extras.platform) || defaultPlatform;
                          const isOverride = rowPlatform !== defaultPlatform;
                          return (
                            <tr key={r.id} className={`border-t ${ROW_FILL[r.status] || ''} ${isOverride ? 'outline outline-2 outline-orange-400 -outline-offset-2' : ''}`} data-testid={`exec-row-${r.id}`}>
                              <td className="px-2 py-1 text-center text-gray-500 align-top">{i + 1}{locked && <Lock size={10} className="inline ml-1 text-gray-400" />}</td>
                              <td className="px-2 py-1 align-top">
                                {rowCard ? (
                                  <div className="flex items-center gap-1">
                                    <button type="button" onClick={() => copy(rowCard.card_number, 'Card')} className="px-2 py-1 rounded bg-gray-50 border border-gray-200 hover:bg-blue-50 hover:border-blue-300 inline-flex items-center gap-1 font-mono text-[11px] text-gray-700" title="Click to copy full card number — full details (Exp, CVV, Pwd) card group header me hain" data-testid={`row-${r.id}-card-no`}>
                                      …{rowCard.last4 || (rowCard.card_number || '').slice(-4)}
                                      <Copy size={9} className="text-gray-400" />
                                    </button>
                                    {!locked && (
                                      <select
                                        value={r.card_id || ''}
                                        onChange={(e) => updateRow(r.id, { card_id: e.target.value || null })}
                                        className="h-5 text-[10px] border rounded px-0.5 font-mono bg-white"
                                        title="Change card"
                                        data-testid={`row-${r.id}-card-picker`}
                                      >
                                        <option value="">⇄</option>
                                        {(cardList || []).map(c => (
                                          <option key={c.id} value={c.id}>…{c.last4} {c.card_holder ? `· ${c.card_holder}` : ''}</option>
                                        ))}
                                      </select>
                                    )}
                                  </div>
                                ) : locked ? (
                                  <span className="text-gray-400 text-xs italic">No card</span>
                                ) : (
                                  <select
                                    value={r.card_id || ''}
                                    disabled={locked}
                                    onChange={(e) => updateRow(r.id, { card_id: e.target.value || null })}
                                    className="h-7 text-xs border border-amber-300 bg-amber-50 rounded px-1 font-mono"
                                    data-testid={`row-${r.id}-card-picker`}
                                  >
                                    <option value="">⚠️ Assign card…</option>
                                    {(cardList || []).map(c => (
                                      <option key={c.id} value={c.id}>…{c.last4} {c.card_holder ? `· ${c.card_holder}` : ''}</option>
                                    ))}
                                  </select>
                                )}
                              </td>
                              <td className="px-2 py-1 align-top">
                                <button type="button" onClick={() => r.consumer_id && copy(r.consumer_id, 'Consumer ID')} className="px-2 py-1 rounded bg-gray-50 border border-gray-200 hover:bg-blue-50 hover:border-blue-300 inline-flex items-center gap-1 font-mono text-xs w-full text-left" title="Click to copy" data-testid={`row-${r.id}-cid-copy`}>
                                  <span className="flex-1">{r.consumer_id || '—'}</span>
                                  {r.consumer_id && <Copy size={10} className="text-gray-400" />}
                                </button>
                              </td>
                              <td className="px-2 py-1 align-top">
                                <button type="button" onClick={() => r.mobile && copy(r.mobile, 'Mobile')} className="px-2 py-1 rounded bg-gray-50 border border-gray-200 hover:bg-blue-50 hover:border-blue-300 inline-flex items-center gap-1 font-mono text-xs w-32 text-left" title="Click to copy" data-testid={`row-${r.id}-mob-copy`}>
                                  <span className="flex-1">{r.mobile || '—'}</span>
                                  {r.mobile && <Copy size={10} className="text-gray-400" />}
                                </button>
                              </td>
                              <td className="px-2 py-1 text-right align-top">
                                <Input type="number" value={r.amount || ''} disabled={locked || isStaffOrSub} title={isStaffOrSub ? 'Agency-set rate — staff cannot edit' : ''} onChange={e => updateRow(r.id, { amount: Number(e.target.value) || 0 })} className={`h-7 text-xs text-right w-24 ${isStaffOrSub ? 'bg-gray-50 text-gray-700 font-semibold cursor-not-allowed' : ''}`} />
                              </td>
                              <td className="px-2 py-1 align-top">
                                <div className="flex items-center gap-1">
                                  <select value={r.status} disabled={locked} onChange={e => updateRow(r.id, { status: e.target.value })} className={`px-1 py-0.5 border rounded text-xs font-medium ${STATUS_COLOR[r.status] || ''}`}>
                                    <option value="success">Success</option>
                                    <option value="pending">Pending</option>
                                    <option value="failed">Failed</option>
                                  </select>
                                  <select
                                    value={rowPlatform}
                                    disabled={locked}
                                    onChange={e => updateRow(r.id, { booking_platform: e.target.value })}
                                    className={`px-1 py-0.5 border rounded text-[10px] font-medium ${isOverride ? 'border-orange-400 bg-orange-50 text-orange-900' : 'border-gray-300'}`}
                                    title={isOverride ? `Override: ${rowPlatform} (default is ${defaultPlatform})` : `Default: ${rowPlatform}`}
                                    data-testid={`row-${r.id}-platform-picker`}
                                  >
                                    {PLATFORM_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                                  </select>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Booking Sheet — Sheet View (Google Sheets style with card grouping) */}
      {viewMode === 'sheet' && (
        <div className="space-y-3">
          {loading && <div className="text-center text-gray-400 py-6 bg-white border rounded">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-center text-gray-400 py-6 bg-white border rounded">
              {rows.length === 0 ? 'Koi row nahi — Staff ne abhi distribute nahi kiya' : 'No match'}
            </div>
          )}
          {!loading && filtered.length > 0 && (() => {
            // Build groups of `groupSize` consecutive filtered rows
            const allGroups = [];
            for (let i = 0; i < filtered.length; i += groupSize) {
              allGroups.push(filtered.slice(i, i + groupSize));
            }
            // Item 4: auto-hide fully-submitted groups (optional toggle).
            // Partial groups stay visible so staff can finish them.
            // Sub_staff me bhi same flow: pending_review (handed off) rows are
            // treated as "done" so the group auto-shifts up, just like
            // submitted rows for main staff.
            const isDone = (r) => !!r.submitted_at || r.phase_status === 'pending_review' || r.phase_status === 'submitted';
            const groups = hideSubmittedGroups
              ? allGroups.filter(gr => gr.length === 0 || gr.some(r => !isDone(r)))
              : allGroups;
            const hiddenCount = allGroups.length - groups.length;
            const PALETTE = ['cyan', 'pink', 'amber', 'emerald', 'violet', 'sky', 'rose', 'lime'];

            // Smart capacity-aware account & card assignment.
            // Walks through groups in order, picking the next account that still
            // has unmet capacity (limit - usage). Already-fully-used accounts
            // are skipped. The same account can be used across multiple groups
            // ONLY if it still has capacity; otherwise the next account is picked.
            //
            // Fallback: if `accList` is empty (e.g. sub_staff with only ONE
            // distributed account that the list endpoint hasn't fetched yet),
            // include the top-level `account` so per-group widgets still show
            // TOTP / Login Password instead of "no secret".
            const sourceAccList = (accList && accList.length > 0)
              ? accList
              : (account ? [account] : []);
            const accPool = sourceAccList
              .filter(a => !a.in_cooldown && (a.available || 0) > 0)
              .sort((a, b) => (b.available || 0) - (a.available || 0));
            const cardPool = (cardList || [])
              .filter(c => !c.in_cooldown && (c.available || 0) > 0)
              .sort((c, d) => (d.available || 0) - (c.available || 0));

            // Track remaining capacity per account/card across this rendering pass
            const accRemaining = new Map(accPool.map(a => [a.id, a.available]));
            const cardRemaining = new Map(cardPool.map(c => [c.id, c.available]));

            // Sticky-account fallback (sub_staff/single-account scenario):
            // when pool has just 1 account, the SAME account should auto-appear
            // in every group. We never deplete its `accRemaining` so it shows
            // up for all groups (capacity is still enforced at submit time).
            const stickyAcc = (accPool.length === 1)
              ? accPool[0]
              : (sourceAccList[0] || null);

            const assignAcc = (rowsCount) => {
              for (const a of accPool) {
                const r = accRemaining.get(a.id) || 0;
                if (r >= 1) {
                  // consume up to rowsCount or whatever is left
                  const take = Math.min(r, rowsCount);
                  // Sticky single-account case: don't deplete so it shows in
                  // every group consistently (user request — auto-assign to
                  // sare groups, same logic as old version).
                  if (accPool.length > 1) {
                    accRemaining.set(a.id, r - take);
                  }
                  return { account: a, take };
                }
              }
              // No capacity? Still return sticky so the email/TOTP show in
              // the group header — submit-time will enforce capacity.
              return { account: stickyAcc, take: 0 };
            };
            const assignCard = (rowsCount) => {
              for (const c of cardPool) {
                const r = cardRemaining.get(c.id) || 0;
                if (r >= 1) {
                  const take = Math.min(r, rowsCount);
                  cardRemaining.set(c.id, r - take);
                  return { card: c, take };
                }
              }
              return { card: null, take: 0 };
            };

            // Pre-compute account/card per group, considering only un-submitted rows
            const groupAlloc = groups.map((groupRows) => {
              const eligible = groupRows.filter(r => !r.submitted_at).length;
              return {
                rowsNeeded: eligible,
                acc: assignAcc(eligible || 1),
                crd: assignCard(eligible || 1),
              };
            });

            return (
              <>
                {hiddenCount > 0 && (
                  <div className="bg-emerald-50 border border-emerald-300 rounded p-2 text-xs flex items-center gap-2" data-testid="hidden-groups-banner">
                    <CheckCircle size={14} className="text-emerald-700" weight="fill" />
                    <span className="font-bold text-emerald-800">{hiddenCount} group{hiddenCount > 1 ? 's' : ''} fully submitted</span>
                    <span className="text-emerald-700">· Hidden from view (upper groups auto-shifted)</span>
                    <button onClick={() => setHideSubmittedGroups(false)} className="ml-auto text-emerald-700 hover:underline font-bold">Show submitted</button>
                  </div>
                )}
                {groups.map((groupRows, gIdx) => {
              const groupColor = PALETTE[gIdx % PALETTE.length];
              const alloc = groupAlloc[gIdx];
              const groupCard = groupCards[gIdx] || alloc.crd.card || card;
              const groupAccount = groupAccounts[gIdx] || alloc.acc.account;
              const groupAccData = groupAccount?.data || {};
              // Smart key detection — same logic as top-level Active Account widget.
              // Skip admin-only keys (Linked Gmail / Recovery Email / Created By)
              // and prefer Acount/Account before generic 'email' substring.
              const _grpIsAdminOnly = (k) => /(linkedgmail|recoveryemail|createdby)/i.test((k || '').toLowerCase().replace(/\s/g, ''));
              const _grpKeys = Object.keys(groupAccData).filter(k => !_grpIsAdminOnly(k));
              const _grpFind = (...patterns) => {
                for (const p of patterns) {
                  const k = _grpKeys.find(k => k.toLowerCase().replace(/[^a-z0-9]/g, '').includes(p));
                  if (k) return k;
                }
                return null;
              };
              let groupAccEmailKey = _grpFind('acount', 'account', 'email');
              const _grpAllPwKeys = _grpKeys.filter(k => {
                const kl = k.toLowerCase().replace(/[^a-z0-9]/g, '');
                return kl.includes('password') || kl.includes('pasword') || kl.includes('pswd');
              });
              let groupAccPwKey = _grpAllPwKeys[0] || null;
              let groupAccPw2Key = _grpAllPwKeys[1] || null;
              let groupAccOtpKey = _grpFind('totpsecret', 'otpcod', 'otpcode', 'totp', 'otp', 'secret');
              // Value-pattern fallback for live-env weirdness
              const _gIsEmail = (v) => /^[\w.+-]+@[\w-]+\.[\w.-]+$/i.test(String(v || '').trim());
              const _gIsPin = (v) => /^\d{4,8}$/.test(String(v || '').trim());
              const _gIsBase32 = (v) => { const s = String(v || '').replace(/\s/g, '').toUpperCase(); return s.length >= 16 && /^[A-Z2-7=]+$/.test(s); };
              const _gClaim = new Set([groupAccEmailKey, groupAccPwKey, groupAccPw2Key, groupAccOtpKey].filter(Boolean));
              if (!groupAccEmailKey) {
                groupAccEmailKey = _grpKeys.find(k => !_gClaim.has(k) && _gIsEmail(groupAccData[k])) || _grpKeys.find(k => !_gClaim.has(k) && _gIsEmail(k));
                if (groupAccEmailKey) _gClaim.add(groupAccEmailKey);
              }
              if (!groupAccOtpKey) {
                groupAccOtpKey = _grpKeys.find(k => !_gClaim.has(k) && _gIsBase32(groupAccData[k])) || _grpKeys.find(k => !_gClaim.has(k) && _gIsBase32(k));
                if (groupAccOtpKey) _gClaim.add(groupAccOtpKey);
              }
              if (!groupAccPwKey) {
                groupAccPwKey = _grpKeys.find(k => !_gClaim.has(k) && _gIsPin(groupAccData[k])) || _grpKeys.find(k => !_gClaim.has(k) && _gIsPin(k));
                if (groupAccPwKey) _gClaim.add(groupAccPwKey);
              }
              const _gResolve = (k, classifier) => {
                if (!k) return '';
                const v = groupAccData[k];
                const keyLooks = classifier(k); const valLooks = classifier(v);
                if (keyLooks && !valLooks) return k;
                if (!keyLooks && valLooks) return v || '';
                if (keyLooks && valLooks) return v || k;
                return v || '';
              };
              const groupAccEmail = _gResolve(groupAccEmailKey, _gIsEmail);
              const groupAccSecret = _gResolve(groupAccOtpKey, _gIsBase32);
              const groupAccPw = _gResolve(groupAccPwKey, _gIsPin);
              const groupAccPw2 = groupAccPw2Key ? (groupAccData[groupAccPw2Key] || groupAccPw2Key) : '';
              const groupSuccess = groupRows.filter(r => r.status === 'success').length;
              // Item: Per-group total amount, prebalance, card load, copy text
              const groupTotal = groupRows.reduce((s, r) => s + Number(r.amount || 0), 0);
              const prebal = Number(groupPrebalance[gIdx] || 0);
              const cardLoad = Math.max(groupTotal - prebal, 0);
              const buildGroupCopy = () => {
                // EXACT user-requested format (Feb 2026):
                // Amazon account / login password / existing balance / balance to be added / card details / card password
                // Each piece on its own line; consumer IDs follow at the bottom.
                const lines = [];
                lines.push(`Amazon account: ${groupAccEmail || '-'}`);
                const loginPw = groupAccPw || groupAccPw2 || '-';
                lines.push(`Login password: ${loginPw}`);
                lines.push(`Existing balance: ₹${roundUp(prebal)}`);
                lines.push(`Balance to be added: ₹${roundUp(cardLoad)}`);
                if (groupCard) {
                  const cardLast = groupCard.card_number || `****${groupCard.last4 || ''}`;
                  const exp = groupCard.expiry ? ` · Exp ${groupCard.expiry}` : '';
                  const cvv = groupCard.cvv ? ` · CVV ${groupCard.cvv}` : '';
                  lines.push(`Card details: ${cardLast}${exp}${cvv}`);
                  lines.push(`Card password: ${groupCard.card_password || '-'}`);
                } else {
                  lines.push(`Card details: -`);
                  lines.push(`Card password: -`);
                }
                lines.push('');
                lines.push('Consumer IDs:');
                groupRows.forEach((r, i) => {
                  const stIcon = r.status === 'success' ? '✅' : r.status === 'failed' ? '❌' : r.status === 'pending' ? '⏳' : '⬜';
                  lines.push(`${i + 1}. ${stIcon} ${r.consumer_id || '(empty)'} ${r.mobile ? '· ' + r.mobile : ''} · ₹${Math.round(Number(r.amount || 0))}`);
                });
                return lines.join('\n');
              };
              const headerColors = {
                cyan: 'bg-cyan-50 border-cyan-300',
                pink: 'bg-pink-50 border-pink-300',
                amber: 'bg-amber-50 border-amber-300',
                emerald: 'bg-emerald-50 border-emerald-300',
                violet: 'bg-violet-50 border-violet-300',
                sky: 'bg-sky-50 border-sky-300',
                rose: 'bg-rose-50 border-rose-300',
                lime: 'bg-lime-50 border-lime-300',
              };
              const dotColors = {
                cyan: 'bg-cyan-500', pink: 'bg-pink-500', amber: 'bg-amber-500',
                emerald: 'bg-emerald-500', violet: 'bg-violet-500', sky: 'bg-sky-500',
                rose: 'bg-rose-500', lime: 'bg-lime-500',
              };
              const chipColors = {
                cyan: 'bg-cyan-100 text-cyan-700', pink: 'bg-pink-100 text-pink-700',
                amber: 'bg-amber-100 text-amber-700', emerald: 'bg-emerald-100 text-emerald-700',
                violet: 'bg-violet-100 text-violet-700', sky: 'bg-sky-100 text-sky-700',
                rose: 'bg-rose-100 text-rose-700', lime: 'bg-lime-100 text-lime-700',
              };
              return (
                <div key={gIdx} className={`border-2 rounded-lg overflow-hidden ${headerColors[groupColor]}`} data-testid={`group-${gIdx}`}>
                  {/* Group Header — REORDERED per user spec (Feb 2026):
                      Amazon account → Login Pwd → TOTP → Card → Exp → CVV → Card Pwd
                      Each field has a visible Copy icon — click anywhere on the cell to copy. */}
                  <div className="px-3 py-2 border-b bg-white/60 grid grid-cols-2 md:grid-cols-9 gap-2 text-xs items-center">
                    <div className="font-bold text-gray-700">
                      <span className={`inline-block w-2 h-2 rounded-full ${dotColors[groupColor]} mr-1.5`} />
                      Group #{gIdx + 1}
                      <div className="text-[10px] text-gray-500 font-normal">{groupSuccess}/{groupRows.length} done</div>
                    </div>
                    {/* 1. Amazon Account email — bold + larger font for visibility */}
                    <div className="cursor-pointer hover:bg-white p-1 rounded col-span-2 group/copy flex items-center gap-2" title="Click email to copy">
                      <div className="flex-1 min-w-0" onClick={() => copy(groupAccEmail, 'Amazon account')}>
                        <div className="text-[9px] text-gray-500 truncate">📧 Amazon account</div>
                        <div className="font-mono text-[13px] font-bold text-gray-900 truncate flex items-center gap-1">
                          <span className="truncate">{groupAccEmail || '—'}</span>
                          <Copy size={11} className="text-gray-400 shrink-0" />
                        </div>
                      </div>
                      {/* Item #3 (Feb 2026): Eye toggle. Default state = SHOWN
                          (user request — credentials hamesha visible rahein).
                          Admin can still click "Hide" to temporarily collapse
                          (useful for screen-recording). `undefined` ⇒ visible. */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setExpandedCreds(p => ({ ...p, [gIdx]: p[gIdx] === false })); }}
                        className={`shrink-0 inline-flex items-center gap-0.5 px-1.5 py-1 rounded text-[10px] font-bold border transition-colors ${expandedCreds[gIdx] === false ? 'bg-violet-600 text-white border-violet-700 hover:bg-violet-700' : 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'}`}
                        title={expandedCreds[gIdx] === false ? 'Show credentials (password, card, etc.)' : 'Hide credentials'}
                        data-testid={`group-${gIdx}-toggle-creds`}
                      >
                        {expandedCreds[gIdx] === false ? <><Eye size={11} weight="bold" /> Show</> : <><EyeSlash size={11} weight="bold" /> Hide</>}
                      </button>
                    </div>
                    {/* Default = expanded. Collapsed only when explicit false. */}
                    {expandedCreds[gIdx] === false ? (
                      <div className="col-span-6 p-1 text-[10px] italic text-gray-400 bg-gray-50 border border-gray-200 rounded text-center">
                        🔒 Login password, TOTP, Card & CVV hidden — click <b>Show</b> button to view credentials
                      </div>
                    ) : (
                      <>
                    {/* 2. Login Password (combined password + live OTP) */}
                    <div className="p-1 col-span-2 md:col-span-1">
                      <div className="text-[9px] text-gray-500">🔑 Login password</div>
                      {(groupAccPw || groupAccPw2) && groupAccSecret ? (
                        <div className="space-y-0.5">
                          {groupAccPw && (
                            <LoginPasswordWidget password={groupAccPw} secret={groupAccSecret} label={`G${gIdx + 1} ${groupAccPwKey || 'P1'}`} compact testId={`group-${gIdx}-login-password`} />
                          )}
                          {groupAccPw2 && (
                            <LoginPasswordWidget password={groupAccPw2} secret={groupAccSecret} label={`G${gIdx + 1} ${groupAccPw2Key || 'P2'}`} compact testId={`group-${gIdx}-login-password-2`} />
                          )}
                        </div>
                      ) : <span className="text-[10px] text-gray-400">—</span>}
                    </div>
                    {/* 3. TOTP (admin/owner only) */}
                    <div className={`p-1 ${isStaffOrSub ? 'hidden' : ''}`}>
                      <div className="text-[9px] text-gray-500">🔐 TOTP</div>
                      {groupAccSecret ? <TOTPWidget secret={groupAccSecret} label={`G${gIdx + 1}`} /> : <span className="text-[10px] text-gray-400">no secret</span>}
                    </div>
                    {/* 4. Card mini-info */}
                    <div className="cursor-pointer hover:bg-white p-1 rounded" onClick={() => groupCard && copy(groupCard.card_number, 'Card')} title="Click to copy card number">
                      <div className="text-[9px] text-gray-500">💳 Card</div>
                      <div className="font-mono text-[11px] flex items-center gap-1">
                        <span>{groupCard ? `****${groupCard.last4}` : '—'}</span>
                        {groupCard && <Copy size={10} className="text-gray-400" />}
                      </div>
                    </div>
                    {/* 5. Expiry */}
                    <div className="cursor-pointer hover:bg-white p-1 rounded" onClick={() => groupCard && copy(groupCard.expiry, 'Expiry')} title="Click to copy expiry">
                      <div className="text-[9px] text-gray-500">Exp</div>
                      <div className="font-mono text-[11px] flex items-center gap-1">
                        <span>{groupCard?.expiry || '—'}</span>
                        {groupCard?.expiry && <Copy size={10} className="text-gray-400" />}
                      </div>
                    </div>
                    {/* 6. CVV */}
                    <div className="cursor-pointer hover:bg-white p-1 rounded" onClick={() => groupCard && copy(groupCard.cvv, 'CVV')} title="Click to copy CVV">
                      <div className="text-[9px] text-gray-500">CVV</div>
                      <div className="font-mono text-[11px] flex items-center gap-1">
                        <span>{groupCard?.cvv ? '●●●' : '—'}</span>
                        {groupCard?.cvv && <Copy size={10} className="text-gray-400" />}
                      </div>
                    </div>
                    {/* 7. Card Password — inline header column right after CVV (Feb 2026 user-fix).
                        Set → click-to-copy mini card. Missing → small red "Set" button (admin) or hint. */}
                    <div className="p-1">
                      <div className="text-[9px] text-gray-500">🔑 Card Pwd</div>
                      {groupCard?.card_password ? (
                        <button
                          type="button"
                          onClick={() => copy(groupCard.card_password, `Card ****${groupCard.last4} password`)}
                          className="font-mono text-[11px] flex items-center gap-1 text-purple-800 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded px-1.5 py-0.5 max-w-full"
                          title={`Click to copy card password: ${groupCard.card_password}`}
                          data-testid={`group-${gIdx}-card-password-inline`}
                        >
                          <span className="truncate">{groupCard.card_password}</span>
                          <Copy size={10} className="text-purple-400 shrink-0" />
                        </button>
                      ) : groupCard ? (
                        !isStaffOrSub ? (
                          <button
                            type="button"
                            onClick={async () => {
                              const pwd = window.prompt(`Card ****${groupCard.last4} ka login password set karein:`, '');
                              if (pwd === null) return;
                              try {
                                await api.patch(`/card-stock/${groupCard.id}/password`, { card_password: pwd });
                                toast.success(`Password set for ****${groupCard.last4}`);
                                await fetchCardList();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Set password failed');
                              }
                            }}
                            className="text-[10px] bg-red-600 hover:bg-red-700 text-white font-bold rounded px-1.5 py-0.5"
                            data-testid={`group-${gIdx}-card-password-inline-set`}
                          >+ Set</button>
                        ) : (
                          <span className="text-[10px] text-red-600 italic">missing</span>
                        )
                      ) : <span className="text-[10px] text-gray-400">—</span>}
                    </div>
                      </>
                    )}
                  </div>
                  {/* 7. Card Pwd + live OTP combined widget — shown ONLY when card_password
                      is set. Mirrors the LoginPasswordWidget for accounts so checkouts that
                      ask for "card password + OTP" together can be one-click copied. */}
                  {/* Card Password row (separate banner) — visible by default
                      (user request — hamesha dikhna chahiye). Only collapsed
                      when user explicitly clicks Hide. */}
                  {expandedCreds[gIdx] !== false && groupCard?.card_password && (
                    <div className="px-3 py-1.5 bg-purple-50 border-b border-purple-200 flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-bold text-purple-700 uppercase">🔑 Card Pwd (****{groupCard.last4}):</span>
                      {/* Pure card password copy */}
                      <button
                        type="button"
                        onClick={() => copy(groupCard.card_password, `Card ****${groupCard.last4} password`)}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-white border-2 border-purple-300 rounded font-mono text-xs text-purple-900 hover:bg-purple-100"
                        title="Click to copy card password only"
                        data-testid={`group-${gIdx}-card-password`}
                      >
                        <span className="font-bold">{groupCard.card_password}</span>
                        <Copy size={11} className="text-purple-500" />
                      </button>
                      {/* Card password + live account OTP merged — for "password+OTP" checkout flows */}
                      {groupAccSecret && (
                        <LoginPasswordWidget
                          password={groupCard.card_password}
                          secret={groupAccSecret}
                          label={`Card ****${groupCard.last4} Pwd + OTP`}
                          compact
                          testId={`group-${gIdx}-card-password-otp`}
                        />
                      )}
                      <span className="text-[9px] text-purple-600 italic">Click to copy · live OTP refresh hota rehta hai</span>
                    </div>
                  )}
                  {/* Card Password missing-warning row (ONLY shown when password is unset).
                      When password IS set, the new "Card Pwd + OTP" widget above handles display.
                      Staff/sub_staff see hint; admin/owner gets inline "Set Password" button. */}
                  {expandedCreds[gIdx] !== false && groupCard && !groupCard.card_password ? (
                    <div
                      className="px-3 py-1.5 bg-red-50 border-b border-red-200 flex items-center gap-2"
                      data-testid={`group-${gIdx}-card-password-missing`}
                    >
                      <span className="text-[10px] font-bold text-red-700 uppercase">⚠️ Card Password missing for ****{groupCard.last4}</span>
                      {!isStaffOrSub ? (
                        <button
                          onClick={async () => {
                            const pwd = window.prompt(`Card ****${groupCard.last4} ka login password set karein:`, '');
                            if (pwd === null) return;
                            try {
                              await api.patch(`/card-stock/${groupCard.id}/password`, { card_password: pwd });
                              toast.success(`Password set for ****${groupCard.last4} — refresh ho raha hai`);
                              await fetchCardList();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Set password failed');
                            }
                          }}
                          className="ml-auto text-[10px] bg-red-600 hover:bg-red-700 text-white font-bold rounded px-2 py-0.5"
                          data-testid={`group-${gIdx}-set-card-password`}
                        >
                          Set Password
                        </button>
                      ) : (
                        <span className="ml-auto text-[10px] text-red-700 italic">Admin se card password set karne ko bolo</span>
                      )}
                    </div>
                  ) : null}
                  {/* Per-group quick actions */}
                  <div className="px-3 py-1 bg-white/40 border-b flex items-center gap-2 flex-wrap">
                    {/* Group Total Amount — shown BEFORE card actions per user request. Bold + larger. */}
                    <span className="text-[13px] font-bold text-emerald-900 bg-emerald-100 border-2 border-emerald-400 rounded px-2.5 py-1 shadow-sm" data-testid={`group-${gIdx}-total`} title="Sum of all row amounts in this group">
                      💰 Total ₹{roundUp(groupTotal)}
                    </span>
                    {/* Pre-balance input — staff fills if account has existing balance. BOLD + LARGER per user request. */}
                    <label className="inline-flex items-center gap-1 text-[12px] bg-amber-50 border-2 border-amber-400 rounded px-2 py-1 shadow-sm" title="Account me pehle se balance ho to yahan daalo (e.g. Amazon gift balance)">
                      <span className="text-amber-900 font-bold">🎁 Existing Balance</span>
                      <input
                        type="number" min={0} step={1}
                        value={groupPrebalance[gIdx] ?? ''}
                        onChange={(e) => setGroupPrebalance(p => ({ ...p, [gIdx]: e.target.value }))}
                        placeholder="0"
                        className="w-20 h-6 text-[13px] font-bold px-1.5 border rounded bg-white text-amber-900"
                        data-testid={`group-${gIdx}-prebalance`}
                      />
                    </label>
                    {/* Balance to be added — ALWAYS visible auto-minus math (total − existing balance).
                        Bold + larger so it's instantly readable for the booker. */}
                    <span
                      className={`text-[12px] font-bold rounded px-2 py-1 shadow-sm ${cardLoad > 0 ? 'bg-blue-100 text-blue-900 border-2 border-blue-400' : 'bg-emerald-100 text-emerald-900 border-2 border-emerald-400'}`}
                      data-testid={`group-${gIdx}-card-load`}
                      title="Card se kitna debit hoga — auto = Total − Existing Balance"
                    >
                      💳 Balance to add: ₹{roundUp(cardLoad)}
                      {cardLoad === 0 && prebal > 0 && <span className="ml-1 text-emerald-700">· prebalance covers ✅</span>}
                    </span>
                    {/* Copy button — full group flow (email → password → total → card) */}
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 gap-1 bg-violet-50 border-violet-300 text-violet-700" onClick={() => { copy(buildGroupCopy(), `Group #${gIdx + 1} flow`); }} data-testid={`group-${gIdx}-copy`}>
                      <Copy size={10} /> Copy Group
                    </Button>
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => { setActiveGroupIdx(gIdx); setShowCardPicker(true); fetchCardList(); }} data-testid={`group-${gIdx}-switch-card`}>
                      Switch card
                    </Button>
                    {isStaffOrSub ? (
                      <>
                        {/* ⚡ Quick Switch — 1-click swap with default reason
                            "Account already used" (most common case). No dialog. */}
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-[10px] px-2 bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100 font-bold"
                          onClick={() => quickSwapAccount(gIdx, groupAccount)}
                          disabled={quickSwapping === gIdx}
                          data-testid={`group-${gIdx}-quick-swap-acc`}
                          title="1-click swap — 'Account already used' reason auto-fill, dialog nahi khulega"
                        >
                          {quickSwapping === gIdx ? '⏳ Switching…' : '⚡ Quick Switch'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-[10px] px-2 bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"
                          onClick={() => openSwapDialog(gIdx, groupAccount)}
                          data-testid={`group-${gIdx}-replace-acc`}
                          title="Account skip karke ek naya replacement lo (1-by-1, reason mandatory)"
                        >
                          🔄 Replace Account
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => { setActiveGroupIdx(gIdx); setShowAccPicker(true); fetchAccList(); }} data-testid={`group-${gIdx}-switch-acc`}>
                        Switch account
                      </Button>
                    )}
                    {(() => {
                      const eligible = groupRows.filter(r => !r.submitted_at).length;
                      const submitted = groupRows.filter(r => r.submitted_at).length;
                      if (eligible === 0 && submitted > 0) {
                        return <span className="text-[10px] font-bold text-emerald-700 inline-flex items-center gap-1 bg-emerald-100 border border-emerald-300 rounded px-2 py-0.5"><CheckCircle size={11} weight="fill" /> All {submitted} submitted</span>;
                      }
                      return (
                        <Button
                          size="sm"
                          onClick={() => submitGroup(groupRows, gIdx, { accountId: groupAccount?.id, cardId: groupCard?.id })}
                          disabled={eligible === 0}
                          className="h-6 text-[10px] px-3 bg-emerald-600 hover:bg-emerald-700 gap-1"
                          data-testid={`group-${gIdx}-submit`}
                          title={`Submit ${eligible} pending rows in this group · uses ${groupAccount?.data?.email || groupAccount?.id || 'no acc'}`}
                        >
                          <CheckCircle size={11} weight="fill" /> Submit Group ({eligible})
                          {submitted > 0 && <span className="text-[9px] opacity-80 ml-0.5">· {submitted}/{groupRows.length} done</span>}
                        </Button>
                      );
                    })()}
                    {/* Capacity transparency badges — show post-allocation remaining */}
                    {groupAccount && (
                      <span className="text-[10px] inline-flex items-center gap-0.5 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 text-blue-700" title={`${groupAccount.data?.email || groupAccount.id} · ${groupAccount.daily_usage_count}/${groupAccount.daily_limit} used today`}>
                        🔑 {groupAccount.daily_usage_count}/{groupAccount.daily_limit} used
                        <span className="text-emerald-600 ml-0.5 font-bold">· {Math.max((groupAccount.available || 0) - (alloc.acc.take || 0), 0)} left after this group</span>
                        {groupAccount.in_cooldown && <span className="text-red-600 ml-0.5">· cooldown</span>}
                      </span>
                    )}
                    {groupCard && (
                      <span className="text-[10px] inline-flex items-center gap-0.5 bg-pink-50 border border-pink-200 rounded px-1.5 py-0.5 text-pink-700" title={`Card ****${groupCard.last4} · ${groupCard.daily_usage_count || 0}/${groupCard.daily_limit || 5} used today`}>
                        💳 {groupCard.daily_usage_count || 0}/{groupCard.daily_limit || 5} used
                        <span className="text-emerald-600 ml-0.5 font-bold">· {Math.max((groupCard.available || 0) - (alloc.crd.take || 0), 0)} left after this group</span>
                        {(groupCard.balance_remaining != null) && <span className="text-pink-500 ml-0.5">· ₹{Math.round(Number(groupCard.balance_remaining))}</span>}
                      </span>
                    )}
                    <span className="text-[10px] text-gray-500 ml-auto">
                      Cards (****{groupCard?.last4 || '—'}) auto-rotate per group · Klick to copy any field
                    </span>
                  </div>
                  {/* Rows for this group */}
                  <div className="overflow-x-auto bg-white">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
                        <tr>
                          <th className="px-2 py-1.5 w-8 text-center">
                            <input
                              type="checkbox"
                              checked={groupRows.length > 0 && groupRows.every(r => selectedRowIds.has(r.id))}
                              onChange={() => toggleAllInGroup(groupRows)}
                              data-testid={`group-${gIdx}-select-all`}
                              title="Select all rows in this group"
                            />
                          </th>
                          <th className="px-2 py-1.5 w-12 text-center">#</th>
                          <th className="px-2 py-1.5 text-left">Consumer ID</th>
                          <th className="px-2 py-1.5 text-left">Mobile</th>
                          <th className="px-2 py-1.5 text-left">Customer</th>
                          <th className="px-2 py-1.5 text-right">Amount</th>
                          <th className="px-2 py-1.5 text-left">Status</th>
                          <th className="px-2 py-1.5 text-left">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupRows.map((r, i) => {
                          const locked = r.submitted_at;
                          const globalIdx = gIdx * groupSize + i + 1;
                          return (
                            <Fragment key={r.id}>
                            <tr className={`border-t ${selectedRowIds.has(r.id) ? 'bg-violet-50/50 ' : ''}${ROW_FILL[r.status] || ''}`} data-testid={`exec-row-${r.id}`}>
                              <td className="px-2 py-1 text-center">
                                <input
                                  type="checkbox"
                                  checked={selectedRowIds.has(r.id)}
                                  onChange={() => toggleRow(r.id)}
                                  disabled={!!locked}
                                  data-testid={`select-row-${r.id}`}
                                />
                              </td>
                              <td className="px-2 py-1 text-center text-gray-500 text-[10px] font-mono">
                                <span className={`inline-block px-1 rounded ${chipColors[groupColor]} font-bold mr-1`}>{i + 1}/{groupSize}</span>
                                <span className="text-gray-400">#{globalIdx}</span>
                                {locked && <Lock size={10} className="inline ml-1 text-gray-400" />}
                              </td>
                              <td className="px-2 py-1">
                                <div className="flex items-center gap-1">
                                  <div className="flex-1">
                                    <ProtectedCell
                                      rowId={r.id}
                                      field="consumer_id"
                                      value={r.consumer_id || ''}
                                      isEditing={isEditing(r.id, 'consumer_id')}
                                      onStartEdit={() => startEdit(r.id, 'consumer_id')}
                                      onStopEdit={stopEdit}
                                      onChange={(v) => updateRow(r.id, { consumer_id: v })}
                                      onCellKeyDown={handleCellKeyDown}
                                      locked={locked}
                                      copy={copy}
                                      copyLabel="Consumer ID"
                                      mono
                                      placeholder="17-digit"
                                      testIdPrefix="cell"
                                    />
                                  </div>
                                  <RowAuditPopover rowId={r.id} hasData={!!(r.submitted_at || r.account_id || r.card_id)} />
                                </div>
                              </td>
                              <td className="px-2 py-1">
                                <ProtectedCell
                                  rowId={r.id}
                                  field="mobile"
                                  value={r.mobile || ''}
                                  isEditing={isEditing(r.id, 'mobile')}
                                  onStartEdit={() => startEdit(r.id, 'mobile')}
                                  onStopEdit={stopEdit}
                                  onChange={(v) => updateRow(r.id, { mobile: v })}
                                  onCellKeyDown={handleCellKeyDown}
                                  locked={locked}
                                  copy={copy}
                                  copyLabel="Mobile"
                                  mono
                                  width="w-32"
                                />
                              </td>
                              <td className="px-2 py-1">
                                <ProtectedCell
                                  rowId={r.id}
                                  field="customer_name"
                                  value={r.customer_name || ''}
                                  isEditing={isEditing(r.id, 'customer_name')}
                                  onStartEdit={() => startEdit(r.id, 'customer_name')}
                                  onStopEdit={stopEdit}
                                  onChange={(v) => updateRow(r.id, { customer_name: v })}
                                  onCellKeyDown={handleCellKeyDown}
                                  locked={locked}
                                  copy={copy}
                                  copyLabel="Customer"
                                />
                              </td>
                              <td className="px-2 py-1 text-right">
                                <Input data-nav-id={`nav-${r.id}-amount`} onKeyDown={(e) => handleCellKeyDown(e, r.id, 'amount')} type="number" value={r.amount || ''} disabled={locked || isStaffOrSub} title={isStaffOrSub ? 'Agency-set rate — staff cannot edit' : ''} onChange={e => updateRow(r.id, { amount: Number(e.target.value) || 0 })} className={`h-7 text-xs text-right w-24 ${isStaffOrSub ? 'bg-gray-50 text-gray-700 font-semibold cursor-not-allowed' : ''}`} />
                                {/* Smart Card Row Split (Feb 2026): chunk indicator
                                    — auto-claimed chunk row from /api/card-rows. */}
                                {(() => {
                                  const myChunks = cardChunks.filter(c =>
                                    (user?.role !== 'sub_staff' || c.assigned_to_sub_staff_id === user?.id) &&
                                    (c.consumer_id === r.consumer_id || c.consumer_id == null)
                                  );
                                  // Bound chunk to this row by global row index within sub_staff slice
                                  const myAvail = myChunks.filter(c => c.status !== 'used');
                                  if (myAvail.length === 0) return null;
                                  // pick first available — display only
                                  const ch = myAvail[0];
                                  return (
                                    <div className="text-[9px] text-violet-700 font-bold mt-0.5" title={`Chunk #${ch.chunk_index}/${ch.total_chunks} of card ****${(ch.card_number || '').slice(-4)}`}>
                                      📦 #{ch.chunk_index}/{ch.total_chunks}
                                    </div>
                                  );
                                })()}
                              </td>
                              <td className="px-2 py-1">
                                <select data-nav-id={`nav-${r.id}-status`} onKeyDown={(e) => handleCellKeyDown(e, r.id, 'status')} value={r.status} disabled={locked} onChange={e => updateRow(r.id, { status: e.target.value })} className={`px-1 py-0.5 border rounded text-xs font-medium ${STATUS_COLOR[r.status] || ''}`}>
                                  <option value="success">Success</option>
                                  <option value="pending">Pending</option>
                                  <option value="failed">Failed</option>
                                </select>
                              </td>
                              <td className="px-2 py-1"><Input data-nav-id={`nav-${r.id}-notes`} onKeyDown={(e) => handleCellKeyDown(e, r.id, 'notes')} value={r.notes || ''} disabled={locked} onChange={e => updateRow(r.id, { notes: e.target.value })} className="h-7 text-xs" placeholder={r.status === 'failed' ? 'Reason*' : 'Optional'} /></td>
                            </tr>
                            {/* Extras row — shown only if Excel had extra columns */}
                            {r.extras && Object.keys(r.extras).length > 0 && (
                              <tr key={`${r.id}-extras`} className="border-t border-dashed">
                                <td colSpan="2" className="px-2 py-1 text-right text-[9px] text-gray-400 uppercase tracking-wider">+extras</td>
                                <td colSpan="6" className="px-2 py-1">
                                  <div className="flex flex-wrap gap-1.5" data-testid={`extras-${r.id}`}>
                                    {Object.entries(r.extras).map(([k, v]) => (
                                      <button
                                        key={k}
                                        type="button"
                                        onClick={() => copy(String(v), k)}
                                        className="inline-flex items-center gap-1 text-[10px] bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-full px-2 py-0.5 cursor-copy"
                                        title={`Click to copy ${k}: ${v}`}
                                        data-testid={`extra-${r.id}-${k}`}
                                      >
                                        <span className="text-blue-700 font-semibold">{k}:</span>
                                        <span className="font-mono text-gray-800 truncate max-w-[200px]">{String(v)}</span>
                                        <Copy size={10} className="text-blue-400" />
                                      </button>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
              </>
            );
          })()}
        </div>
      )}

      <div className="text-[10px] text-gray-400"><FloppyDisk size={10} className="inline mr-1" /> Auto-save ON · Default Success · Manual change for Pending/Failed</div>

      {/* Account Picker */}
      <Dialog open={showAccPicker} onOpenChange={setShowAccPicker}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{viewMode === 'sheet' ? `Switch Account for Group #${activeGroupIdx + 1}` : 'Switch Account'}</DialogTitle></DialogHeader>
          <Input placeholder="Search by email…" value={accSearch} onChange={e => { setAccSearch(e.target.value); fetchAccList(e.target.value); }} className="mb-2" />
          <div className="max-h-96 overflow-y-auto space-y-1">
            {accList.length === 0 ? <div className="text-sm text-gray-400 py-3 text-center">Koi account nahi mila.</div>
              : accList.map(a => (
                <div key={a.id} className={`p-2 rounded border flex items-center justify-between ${a.in_cooldown ? 'bg-red-50 border-red-200 opacity-60' : 'bg-white border-gray-200 hover:bg-blue-50 cursor-pointer'}`} onClick={() => {
                  if (a.in_cooldown) return;
                  if (viewMode === 'sheet') {
                    setGroupAccounts(prev => ({ ...prev, [activeGroupIdx]: a }));
                    setShowAccPicker(false);
                    toast.success(`Group #${activeGroupIdx + 1} → ${a.data?.email || a.data?.Email}`);
                  } else {
                    switchAccount(a.id);
                  }
                }}>
                  <div>
                    <div className="font-mono text-xs">{a.data?.email || a.data?.Email || '-'}</div>
                    <div className="text-[10px] text-gray-500">Used: {a.daily_usage_count}/{a.daily_limit} · {a.in_cooldown ? `🔒 Cooldown till ${new Date(a.cooldown_until).toLocaleDateString()}` : '✅ Available'}</div>
                  </div>
                  {a.is_active_for_user && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">ACTIVE</span>}
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Account Swap Dialog — staff/sub_staff 1-by-1 replace with mandatory reason */}
      <Dialog open={!!swapDialog} onOpenChange={(o) => { if (!o) setSwapDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="swap-account-dialog">
          <DialogHeader>
            <DialogTitle>Replace Account · Group #{(swapDialog?.groupIdx ?? 0) + 1}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-300 rounded p-2 text-[11px] text-amber-900">
              <b>Confirm karne se pehle dhyan do:</b> Current account release ho jayega aur ek hi naya replacement milega
              (bulk list nahi). Naye account ke details se hi booking submit hogi.
            </div>
            {swapDialog?.current && (
              <div className="text-xs bg-gray-50 border rounded p-2">
                <span className="text-gray-500">Skipping: </span>
                <span className="font-mono">{swapDialog.current?.data?.email || swapDialog.current?.id}</span>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-gray-700">Reason <span className="text-red-500">*</span></label>
              <select
                value={swapReason}
                onChange={e => setSwapReason(e.target.value)}
                className="w-full h-9 text-sm mt-1 rounded-md border border-gray-300 px-2 bg-white"
                data-testid="swap-reason"
              >
                <option value="">— Select a reason —</option>
                {SWAP_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            {swapReason === 'Other' && (
              <div>
                <label className="text-xs font-semibold text-gray-700">Specific reason <span className="text-red-500">*</span></label>
                <textarea
                  value={swapExtra}
                  onChange={e => setSwapExtra(e.target.value)}
                  placeholder="Detail likho (3+ characters)…"
                  className="w-full text-sm rounded-md border border-gray-300 p-2 h-20 mt-1"
                  data-testid="swap-reason-extra"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSwapDialog(null)} data-testid="swap-cancel">Cancel</Button>
            <Button
              onClick={confirmSwap}
              disabled={swapping || !swapReason}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="swap-confirm"
            >
              {swapping ? 'Swapping…' : 'Confirm & Replace'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Card Picker */}
      <Dialog open={showCardPicker} onOpenChange={setShowCardPicker}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{viewMode === 'sheet' ? `Switch Card for Group #${activeGroupIdx + 1}` : 'Switch Card'}</DialogTitle></DialogHeader>
          <Input placeholder="Search by last 4 / nickname…" value={cardSearch} onChange={e => { setCardSearch(e.target.value); fetchCardList(e.target.value); }} className="mb-2" />
          <div className="max-h-96 overflow-y-auto space-y-1">
            {cardList.length === 0 ? <div className="text-sm text-gray-400 py-3 text-center">Koi card nahi mila.</div>
              : cardList.map(c => (
                <div key={c.id} className={`p-2 rounded border flex items-center justify-between ${c.in_cooldown ? 'bg-red-50 border-red-200 opacity-60' : 'bg-white border-gray-200 hover:bg-purple-50 cursor-pointer'}`} onClick={() => {
                  if (c.in_cooldown) return;
                  if (viewMode === 'sheet') {
                    setGroupCards(prev => ({ ...prev, [activeGroupIdx]: c }));
                    setShowCardPicker(false);
                    toast.success(`Group #${activeGroupIdx + 1} → card ****${c.last4}`);
                  } else {
                    setCard(c);
                    setShowCardPicker(false);
                  }
                }}>
                  <div>
                    <div className="font-mono text-xs">****{c.last4} · {c.card_holder}</div>
                    <div className="text-[10px] text-gray-500">
                      Used: {c.daily_usage_count}/{c.daily_limit} · 
                      <span className={`font-bold ${Number(c.available_limit || 0) > 0 ? 'text-emerald-700' : 'text-red-600'}`}> Balance: {fmt(c.available_limit)}</span>
                      {c.initial_limit > 0 && c.initial_limit !== c.available_limit && (
                        <span className="text-gray-400"> / {fmt(c.initial_limit)}</span>
                      )}
                    </div>
                  </div>
                  {card?.id === c.id && <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">ACTIVE</span>}
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Rows dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus size={18} /> Add Rows — Paste Consumer IDs / Mobile Numbers</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-gray-600">
              Ek line me ek consumer ID daalo. Tab ya comma se mobile / name / amount bhi de sakte ho.
            </p>
            <p className="text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded p-1.5">
              💡 <b>Sirf mobile numbers</b> hain (10-digit) toh seedhe paste karo — auto-detect ho jayenge, LPG ID khali rahegi (baad me bhar dena).
            </p>
            <textarea
              rows={8}
              value={pasteRows}
              onChange={e => setPasteRows(e.target.value)}
              placeholder={`123456789012345\n234567890123456\t9876543210\n345678901234567\t9123456789\tRAJ KUMAR\t950`}
              className="w-full border rounded p-2 font-mono text-xs"
              data-testid="ws-add-paste-text"
            />
            <p className="text-[10px] text-gray-400">{pasteRows.split(/\r?\n/).filter(l => l.trim()).length} lines detected</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddRows} className="bg-blue-600 hover:bg-blue-700 gap-1" data-testid="ws-add-confirm-btn">
              <Plus size={14} /> Add Rows
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import from Admin's Checked File */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DownloadSimple size={18} /> Import from Admin's HPCL-Checked Reports
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-gray-600">
              Admin ne HPCL Data Matcher me jo file check ki hai usme se <b>Success</b>-status wale consumer IDs yahan import ho jayenge. Duplicate apne aap skip ho jayenge.
            </p>
            {checkedSources.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-400" data-testid="ws-import-empty">
                Koi checked report available nahi hai. Pehle Admin se HPCL Data Matcher run karwao.
              </div>
            ) : (
              <div className="border rounded divide-y" data-testid="ws-import-list">
                {checkedSources.map((s) => (
                  <div key={s.id} className="px-3 py-2 flex items-center justify-between gap-2 hover:bg-gray-50">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{s.success_filename || s.agency_filename || '(untitled)'}</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">
                        by {s.uploader_name} ({s.uploader_role}) · {s.created_at ? new Date(s.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </div>
                    </div>
                    <span className="text-[11px] font-mono font-bold bg-emerald-100 text-emerald-700 border border-emerald-300 rounded px-2 py-0.5 whitespace-nowrap">
                      {s.success_count} success
                    </span>
                    <Button size="sm" disabled={importing} onClick={() => importFromReport(s.id)} className="bg-violet-600 hover:bg-violet-700 gap-1" data-testid={`ws-import-pick-${s.id}`}>
                      {importing ? '…' : <><DownloadSimple size={12} /> Import</>}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Keyboard shortcuts cheat sheet — press "?" anytime */}
      <KeyboardShortcutsOverlay
        shortcuts={SHEET_SHORTCUTS}
        contextLabel="Booking Workspace"
        tourKey="booking-workspace-v1"
      />

      {/* Auto-Distribute Cards Dialog (Item 7) */}
      <AutoDistributeCardsDialog
        open={showAutoDistribute}
        onOpenChange={setShowAutoDistribute}
        fileId={fileId}
        totalBookings={rows.filter(r => !r.submitted_at).length}
        ratePerCylinder={Number(file?.booking_rate) || 0}
        defaultGroupSize={groupSize}
        agencyId={file?.booking_agency_id}
        onApplied={() => reload()}
      />

      {/* Submit Report Dialog (Item 2) */}
      <SubmitReportDialog
        open={showSubmitReport}
        onOpenChange={setShowSubmitReport}
        fileId={fileId}
        fileName={file?.original_filename || file?.distributor_name}
        rows={rows}
      />
    </div>
  );
}
